/**
 * jbc-x3-configurator.js
 * ----------------------
 * Handles the X3 Configurator page functionality:
 * - Reads config product options and variants from data attributes
 * - Selection state management for dynamic option choices
 * - Finds matching variant based on selections
 * - Price calculation with differences from base price
 * - Availability checking based on component availability
 * - Add single config product variant to cart
 *
 * Data attributes on the configurator section:
 * - data-configurator: JSON object with:
 *   - options: Array of option objects (name, values, stepTitle, stepDescription, valueDescriptions)
 *   - variants: Array of variant objects (id, price, available, componentsAvailable, options, image, title)
 *   - basePrice: Minimum price across all variants
 *
 * Choice buttons have:
 * - data-step: Option name (e.g., "Bar", "Bands", "Ground Plate")
 * - data-option-value: The option value (e.g., "X3 Elite", "Original", "ParaForce")
 */

const CART_UPDATE_EVENT = 'cart:update';
const CONFIGURATOR_SOURCE_ID = 'x3-configurator';
const CART_ADD_URL = '/cart/add.js';
const CART_URL = '/cart.js';
const FALLBACK_RESOLVE_URL = '/apps/jbc/x3-configurator/resolve';
const PREORDER_PROXY_TIMEOUT_MS = 4000;
const PREVIEW_IMAGE_ENTER_DELAY_MS = 140;
const PREVIEW_IMAGE_RESTORE_DELAY_MS = 120;
const RICH_TEXT_BLOCK_TAGS = new Set(['A', 'B', 'BR', 'EM', 'I', 'LI', 'OL', 'P', 'STRONG', 'SUB', 'SUP', 'U', 'UL']);
const RICH_TEXT_INLINE_TAGS = new Set(['B', 'BR', 'EM', 'I', 'STRONG', 'SUB', 'SUP', 'U']);
const RICH_TEXT_DROP_TAGS = new Set([
  'BUTTON',
  'EMBED',
  'FORM',
  'IFRAME',
  'INPUT',
  'MATH',
  'OBJECT',
  'SCRIPT',
  'SELECT',
  'STYLE',
  'SVG',
  'TEMPLATE',
  'TEXTAREA',
]);
const RICH_TEXT_INLINE_BREAK_TAGS = new Set(['DIV', 'LI', 'P']);

async function fetchWithTimeout(url, options, timeoutMs) {
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => {
    controller.abort();
  }, timeoutMs);

  try {
    return await fetch(url, {
      ...options,
      signal: controller.signal,
    });
  } finally {
    window.clearTimeout(timeoutId);
  }
}

class X3Configurator {
  constructor(sectionElement) {
    this.section =
      sectionElement ||
      document.querySelector(
        '.x3-configurator[data-configurator]:not([data-configurator-initialized="true"])'
      );
    if (!this.section) return;

    this.section.dataset.configuratorInitialized = 'true';

    // Parse data from section attributes
    const configuratorData = JSON.parse(this.section.dataset.configurator || '{}');
    this.options = configuratorData.options || [];
    this.variants = configuratorData.variants || [];
    this.componentImages = configuratorData.componentImages || {};
    this.componentAvailability = configuratorData.componentAvailability || {};
    this.preorderSettings = configuratorData.preorderSettings || {};
    this.openBoxSettings = configuratorData.openBoxSettings || {};
    this.forceConfig = configuratorData.force || {};
    this.isOpenBox = configuratorData.openBox === true || this.section.dataset.openBox === 'true';

    this.selections = this.getInitialSelections();

    // Selected add-ons state - keyed by option name, values are arrays of variant IDs
    this.selectedAddons = {};
    this.preorderAccepted = false;
    this.openBoxAcknowledgementAccepted = false;
    this.selectedBeltVariantId = null;
    this.selectedBeltSize = null;
    this.selectedBeltTitle = null;
    this.forcePremiumSelected = this.isForcePremiumConfigured();
    this.previewImageTimer = null;
    this.previewImageSwapId = 0;
    this.cartItemKey = null;
    this.cartCompanionMap = null;
    this.configurationId = null;

    this.init();
  }

  init() {
    this.bindElements();
    this.initLayers();
    this.renderRichTextDescriptions();
    this.bindEvents();
    this.preloadComponentImages();
    this.updatePriceDisplays();
    this.updateAvailability();
    this.updateAddonSections();
    this.updateUI();
    this.updatePreviewImage();
    // If the modal provided a preselect handle, and it matches Force product,
    // preselect the Force bar option so the user sees Force preselected when
    // opening the configurator from the Force PDP.
    try {
      const modal = document.getElementById('x3-configurator-modal');
      const preselectHandle = modal?.dataset?.preselectHandle;
      if (preselectHandle && preselectHandle.includes('x3-force') && this.isForceConfigured()) {
        this.selections[this.forceConfig.barOptionName] = this.forceConfig.barValue;
        this.updateUI();
        this.updatePriceDisplays();
        this.updateAvailability();
      }
    } catch (_) {
      // Ignore preselect initialization errors
    }

    // Listen for preselect events dispatched by the modal. This covers cases
    // where the modal prefetched/initialized the configurator before the
    // page-side open action could set the dataset on the modal.
    this._preselectHandler = (event) => {
      try {
        const handle = event?.detail?.handle;
        if (!handle) return;
        if (handle.includes('x3-force') && this.isForceConfigured()) {
          this.selections[this.forceConfig.barOptionName] = this.forceConfig.barValue;
          this.updateUI();
          this.updatePriceDisplays();
          this.updateAvailability();
        }
      } catch (_) {
        // Ignore preselect event errors
      }
    };
    document.addEventListener('x3-configurator:preselect', this._preselectHandler);

    // Apply cart item selections if the modal was opened from a cart line item.
    // First-load path: read from modal dataset.
    try {
      const modal = document.getElementById('x3-configurator-modal');
      const cartItemKey = modal?.dataset?.x3CartItemKey;
      const cartVariantId = modal?.dataset?.x3CartVariantId;
      const cartBeltVariantId = modal?.dataset?.x3CartBeltVariantId;
      const cartCompanionItems = modal?.dataset?.x3CartCompanionItems;
      const configurationId = modal?.dataset?.x3ConfigurationId;
      this.applyCartEditContext({
        cartItemKey,
        variantId: cartVariantId,
        beltVariantId: cartBeltVariantId,
        companionItems: cartCompanionItems,
        configurationId,
        resetWhenMissing: false,
      });
    } catch (_) {
      // Ignore cart edit initialization errors
    }

    // Subsequent-open path: listen for the event dispatched by the modal on each open.
    this._editCartHandler = (event) => {
      try {
        this.applyCartEditContext({ ...(event?.detail || {}), resetWhenMissing: true });
      } catch (_) {
        // Ignore cart edit event errors
      }
    };
    document.addEventListener('x3-configurator:edit-cart-item', this._editCartHandler);

    this.applyDirectPageCartEditContext();

    this.finishInitialLoading();
  }

  applyCartEditContext(context = {}) {
    const {
      cartItemKey,
      variantId,
      beltVariantId,
      companionItems,
      configurationId,
      resetWhenMissing = true,
    } = context;

    if (cartItemKey && variantId) {
      this.cartItemKey = cartItemKey;
      this.configurationId = configurationId || null;
      this.applyVariantSelections(variantId);

      if (beltVariantId) {
        this.applyBeltSelection(beltVariantId);
      }

      this.applyCartCompanions(companionItems || '');
      this.updateAddToCartButton();
      return true;
    }

    if (resetWhenMissing) {
      this.cartItemKey = null;
      this.configurationId = null;
      this.resetCartCompanions();
      this.updateAddToCartButton();
    }

    return false;
  }

  applyDirectPageCartEditContext() {
    if (!this.isOpenBox || typeof window === 'undefined') return;

    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get('x3_edit') !== '1') return;

      this.applyCartEditContext({
        cartItemKey: params.get('cart_item_key') || '',
        variantId: params.get('variant_id') || '',
        beltVariantId: params.get('belt_variant_id') || '',
        companionItems: params.get('companion_items') || '',
        configurationId: params.get('configuration_id') || '',
        resetWhenMissing: false,
      });
    } catch (_) {
      // Ignore direct-page cart edit initialization errors.
    }
  }

  finishInitialLoading() {
    if (!this.isOpenBox) return;

    const reveal = () => {
      const loadingElement = this.section.querySelector('[data-configurator-loading]');
      this.section.classList.remove('x3-configurator--loading');
      this.section.removeAttribute('aria-busy');
      if (loadingElement) {
        loadingElement.hidden = true;
      }
    };

    if (typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(reveal);
    } else {
      reveal();
    }
  }

  bindElements() {
    this.choiceButtons = this.section.querySelectorAll('.x3-configurator__choice');
    this.totalPrice = this.section.querySelector('[data-configurator-total-price]');
    this.totalComparePrice = this.section.querySelector('[data-configurator-total-compare-price]');
    this.totalSavingsPill = this.section.querySelector('[data-configurator-total-savings-pill]');
    this.addToCartBtn = this.section.querySelector('[data-configurator-add-to-cart]');
    this.addToCartBtnText = this.addToCartBtn?.querySelector('.x3-configurator__add-to-cart-text') || null;
    this.errorContainer = this.section.querySelector('[data-configurator-error]');
    this.previewImage = this.section.querySelector('[data-configurator-preview-image]');
    this.preorderContainer = this.section.querySelector('[data-configurator-preorder]');
    this.preorderItems = this.section.querySelector('[data-configurator-preorder-items]');
    this.preorderCheckbox = this.preorderContainer?.querySelector('.checkbox__input') || null;
    this.preorderCheckboxLabel = this.preorderContainer?.querySelector('.checkbox__label-text') || null;
    this.preorderTitle = this.section.querySelector('[data-configurator-preorder-title]');
    this.openBoxAcknowledgementContainer = this.section.querySelector('[data-open-box-acknowledgement]');
    this.openBoxAcknowledgementCheckbox =
      this.openBoxAcknowledgementContainer?.querySelector('.checkbox__input') || null;
    this.forceBeltStep = this.section.querySelector('[data-force-belt-step]');
    this.forceBeltButtons = this.section.querySelectorAll('[data-force-belt-choice]');
    this.forceBeltCallout = this.section.querySelector('[data-force-belt-callout]');
    this.forceBeltSummary = this.section.querySelector('[data-force-belt-summary]');
    this.forceBeltSummaryValue = this.section.querySelector('[data-force-belt-summary-value]');
    this.forcePremiumStep = this.section.querySelector('[data-force-premium-step]');
    this.forcePremiumUpsell = this.section.querySelector('[data-force-premium-upsell]');
    this.forcePremiumToggle = this.section.querySelector('[data-force-premium-toggle]');
    this.forcePremiumSummary = this.section.querySelector('[data-force-premium-summary]');

    if (this.addToCartBtn) {
      if (!this.addToCartBtnText) {
        this.addToCartBtnText = document.createElement('span');
        this.addToCartBtnText.className = 'x3-configurator__add-to-cart-text';

        const currentText = this.addToCartBtn.textContent.trim();
        this.addToCartBtnText.textContent = currentText || 'Add to Cart';

        Array.from(this.addToCartBtn.childNodes).forEach((node) => {
          if (node.nodeType === Node.TEXT_NODE && node.textContent.trim()) {
            node.remove();
          }
        });

        this.addToCartBtn.appendChild(this.addToCartBtnText);
      }

      if (!this.addToCartBtn.dataset.originalText) {
        this.addToCartBtn.dataset.originalText = this.addToCartBtnText.textContent.trim();
      }
    }

    // Layer compositing elements
    this.layersContainer = this.section.querySelector('[data-configurator-layers]');
    this.layerImages = this.layersContainer
      ? Array.from(this.layersContainer.querySelectorAll('.configurator-layers__img'))
      : [];
    this.layersActive = this.layerImages.length > 0;

    // Crossfade image layers (fallback when layer system is inactive)
    if (this.previewImage) {
      this.imageLayerA = this.previewImage.querySelector('[data-image-layer="a"]');
      this.imageLayerB = this.previewImage.querySelector('[data-image-layer="b"]');
      this.activeLayer = 'a';
    }

    // Add-on elements
    this.addonSections = this.section.querySelectorAll('.x3-configurator__addon-section');
    this.addonCheckboxes = this.section.querySelectorAll('.x3-configurator__addon-checkbox');
    this.addonChoiceOptions = this.section.querySelectorAll('[data-addon-choice-source]');

    if (this.forcePremiumToggle) {
      this.forcePremiumToggle.setAttribute('aria-checked', String(this.forcePremiumSelected));
    }

    if (this.preorderCheckboxLabel) {
      this.preorderCheckboxLabel.textContent =
        this.preorderSettings.checkboxText ||
        'I understand these items are preorders and will ship when available.';
    }

    if (this.preorderTitle) {
      this.preorderTitle.textContent = this.preorderSettings.title || 'Preorder details';
    }
  }

  initLayers() {
    if (!this.layersActive || !this.previewImage) return;

    // Check for empty src on critical layers before relying on onerror.
    // file_url returns "" when a file isn't uploaded, and onerror is unreliable
    // for src="" because browsers resolve it to the current page URL.
    const hasMissingSrc = this.layerImages.some(
      (img) => img.hasAttribute('data-layer-critical') && !img.getAttribute('src')
    );
    if (hasMissingSrc) {
      this.deactivateLayers();
      return;
    }

    this.layerImages.forEach((img) => {
      if (img.hasAttribute('data-layer-critical')) {
        img.onerror = () => this.deactivateLayers();
      } else {
        img.onerror = () => {
          img.style.display = 'none';
        };
      }
    });

    // Images with loading="eager" start fetching before JS runs. If a critical
    // image already failed by the time we attach onerror, the event was missed.
    // complete=true + naturalWidth=0 means the load finished but produced no image.
    const alreadyFailed = this.layerImages.some(
      (img) => img.hasAttribute('data-layer-critical') && img.complete && img.naturalWidth === 0
    );
    if (alreadyFailed) {
      this.deactivateLayers();
      return;
    }

    this.previewImage.classList.add('x3-configurator__preview-image--layers-active');
    this.updateLayers();
  }

  deactivateLayers() {
    this.layersActive = false;
    if (this.previewImage) {
      this.previewImage.classList.remove('x3-configurator__preview-image--layers-active');
      this.previewImage.classList.add('x3-configurator__preview-image--layers-failed');
    }
    this.updatePreviewImage();
  }

  updateLayers() {
    if (!this.layersActive) return;

    const allAddonIds = Object.values(this.selectedAddons).flat();

    this.layerImages.forEach((img) => {
      let visible = false;
      const group = img.dataset.layerGroup;
      const type = img.dataset.layerType;

      if (group) {
        visible = this.selections[group] === img.dataset.layerValue;
      } else if (type === 'addon') {
        const ids = (img.dataset.layerAddonIds || '').split(',');
        visible = ids.some((id) => allAddonIds.includes(id));
      } else if (type === 'belt') {
        visible = Boolean(this.selectedBeltVariantId);
      }

      img.classList.toggle('configurator-layers__img--visible', visible);
    });
  }

  bindEvents() {
    // Choice button clicks
    this.choiceButtons.forEach((btn) => {
      if (btn.matches('[data-force-belt-choice]')) {
        return;
      }

      btn.addEventListener('click', (e) => this.handleChoiceClick(e));
      btn.addEventListener('mouseenter', (e) => this.handleChoicePreviewEnter(e));
      btn.addEventListener('focus', (e) => this.handleChoicePreviewEnter(e));
      btn.addEventListener('mouseleave', () => this.scheduleSelectedVariantPreview());
      btn.addEventListener('blur', () => this.scheduleSelectedVariantPreview());
    });

    // Add-on checkbox changes
    this.addonCheckboxes.forEach((checkbox) => {
      checkbox.addEventListener('change', (e) => this.handleAddonToggle(e));

      const card = checkbox.closest('.x3-configurator__addon-card');
      if (card) {
        card.addEventListener('mouseenter', () => this.handleAddonPreviewEnter(checkbox));
        card.addEventListener('mouseleave', () => this.scheduleSelectedVariantPreview());
      }
      checkbox.addEventListener('focus', () => this.handleAddonPreviewEnter(checkbox));
      checkbox.addEventListener('blur', () => this.scheduleSelectedVariantPreview());
    });

    this.addonChoiceOptions.forEach((option) => {
      option.addEventListener('click', (event) => this.handleAddonChoiceClick(event));
      option.addEventListener('keydown', (event) => this.handleAddonChoiceKeydown(event));
    });

    this.forceBeltButtons.forEach((btn) => {
      btn.addEventListener('click', (e) => this.handleBeltChoiceClick(e));
      btn.addEventListener('mouseenter', (e) => this.handleBeltPreviewEnter(e));
      btn.addEventListener('focus', (e) => this.handleBeltPreviewEnter(e));
      btn.addEventListener('mouseleave', () => this.scheduleSelectedVariantPreview());
      btn.addEventListener('blur', () => this.scheduleSelectedVariantPreview());
    });

    if (this.forcePremiumToggle) {
      this.forcePremiumToggle.addEventListener('click', () => this.handleForcePremiumToggle());
    }

    if (this.preorderCheckbox) {
      this.preorderCheckbox.addEventListener('change', (event) => {
        this.preorderAccepted = event.currentTarget.checked;
        this.updateAddToCartButton();
      });
    }

    if (this.openBoxAcknowledgementCheckbox) {
      this.openBoxAcknowledgementCheckbox.addEventListener('change', (event) => {
        this.openBoxAcknowledgementAccepted = event.currentTarget.checked;
        this.updateAddToCartButton();
      });
    }

    // Add to cart
    if (this.addToCartBtn) {
      this.addToCartBtn.addEventListener('click', () => this.handleAddToCart());
    }
  }

  getInitialSelections() {
    const selections = {};
    const firstAvailableVariant = this.variants.find(
      (variant) => variant.available && variant.componentsAvailable
    );

    this.options.forEach((option, index) => {
      if (!option.values || option.values.length === 0) {
        return;
      }

      const selectedValue = firstAvailableVariant?.options?.[index];
      selections[option.name] =
        selectedValue && option.values.includes(selectedValue) ? selectedValue : option.values[0];
    });

    return selections;
  }

  isForceConfigured() {
    return Boolean(this.forceConfig?.enabled && this.forceConfig.barOptionName && this.forceConfig.barValue);
  }

  isForcePremiumConfigured() {
    const premium = this.forceConfig?.premiumUpsell || {};
    return Boolean(
      premium.enabled &&
        (premium.numericVariantId || premium.variantId) &&
        (premium.numericSellingPlanId || premium.sellingPlanId)
    );
  }

  isForceSelected() {
    if (!this.isForceConfigured()) return false;
    return this.selections[this.forceConfig.barOptionName] === this.forceConfig.barValue;
  }

  getForceBeltOptions() {
    return (this.forceConfig?.beltOptions || []).filter((option) => option.enabled);
  }

  hasCompleteOptionSet() {
    return (
      this.options.length > 0 &&
      this.options.every((option) => Array.isArray(option.values) && option.values.length > 0)
    );
  }

  getNumericId(value) {
    return String(value || '').split('/').pop();
  }

  normalizeVariantReference(reference) {
    if (!reference) {
      return {
        variantId: '',
        numericVariantId: '',
        fallbackEnabled: false,
        fallbackVariantId: '',
        fallbackNumericVariantId: '',
        fallbackMode: 'silent_auto',
        primaryLabel: 'Open Box',
        fallbackLabel: 'New',
      };
    }

    if (typeof reference === 'string' || typeof reference === 'number') {
      const variantId = String(reference || '');
      return {
        variantId,
        numericVariantId: this.getNumericId(variantId),
        fallbackEnabled: false,
        fallbackVariantId: '',
        fallbackNumericVariantId: '',
        fallbackMode: 'silent_auto',
        primaryLabel: 'Open Box',
        fallbackLabel: 'New',
      };
    }

    const variantId = String(reference.variantId || '');
    const fallbackVariantId = String(reference.fallbackVariantId || '');

    const hasFallbackEnabled = Object.prototype.hasOwnProperty.call(reference, 'fallbackEnabled');

    return {
      variantId,
      numericVariantId: String(reference.numericVariantId || this.getNumericId(variantId)),
      fallbackEnabled: hasFallbackEnabled ? reference.fallbackEnabled === true : Boolean(fallbackVariantId),
      fallbackVariantId,
      fallbackNumericVariantId: String(reference.fallbackNumericVariantId || this.getNumericId(fallbackVariantId)),
      fallbackMode: reference.fallbackMode || 'silent_auto',
      primaryLabel: reference.primaryLabel || 'Open Box',
      fallbackLabel: reference.fallbackLabel || 'New',
    };
  }

  getReferenceAvailabilityState(reference) {
    const normalized = this.normalizeVariantReference(reference);
    const primaryId = normalized.numericVariantId || this.getNumericId(normalized.variantId);
    const fallbackId = normalized.fallbackNumericVariantId || this.getNumericId(normalized.fallbackVariantId);
    const primaryKnown =
      Boolean(primaryId) &&
      (!this.isOpenBox || Object.prototype.hasOwnProperty.call(this.componentAvailability, primaryId));
    const primaryAvailable = primaryKnown && this.isComponentInStock(primaryId);
    const fallbackAvailable =
      normalized.fallbackEnabled === true &&
      Boolean(fallbackId) &&
      this.isComponentInStock(fallbackId);
    const selectedVariantId = primaryAvailable
      ? normalized.variantId || primaryId
      : fallbackAvailable
        ? normalized.fallbackVariantId || fallbackId
        : '';

    return {
      ...normalized,
      primaryKnown,
      primaryAvailable,
      fallbackAvailable,
      available: Boolean(selectedVariantId),
      selectedVariantId,
      selectedNumericVariantId: this.getNumericId(selectedVariantId),
      source: primaryAvailable ? 'primary' : fallbackAvailable ? 'fallback' : 'none',
    };
  }

  getDatasetAvailability(element, key, fallbackVariantId) {
    const value = element?.dataset?.[key];
    if (value === 'true') return true;
    if (value === 'false') return false;
    return this.isComponentInStock(fallbackVariantId);
  }

  getAddonAvailabilityState(checkbox) {
    if (!checkbox) {
      return { available: false, primaryAvailable: false, fallbackAvailable: false };
    }

    const primaryId = checkbox.dataset.addonVariantId || '';
    const fallbackId = checkbox.dataset.addonFallbackVariantId || '';
    const primaryAvailable = this.getDatasetAvailability(checkbox, 'addonAvailable', primaryId);
    const fallbackAvailable =
      checkbox.dataset.addonFallbackEnabled === 'true' &&
      Boolean(fallbackId) &&
      this.getDatasetAvailability(checkbox, 'addonFallbackAvailable', fallbackId);

    return {
      primaryAvailable,
      fallbackAvailable,
      available: primaryAvailable || fallbackAvailable,
    };
  }

  isAddonCustomerChoice(checkbox) {
    const fallbackMode = checkbox?.dataset?.addonFallbackMode || 'silent_auto';

    return (
      this.isOpenBox &&
      checkbox?.dataset?.addonFallbackEnabled === 'true' &&
      (fallbackMode === 'customer_choice' || fallbackMode === 'customer_choice_oos') &&
      Boolean(checkbox?.dataset?.addonFallbackVariantId)
    );
  }

  isAddonCustomerChoiceOos(checkbox) {
    return this.isAddonCustomerChoice(checkbox) && checkbox?.dataset?.addonFallbackMode === 'customer_choice_oos';
  }

  getAddonDefaultSource(checkbox) {
    const state = this.getAddonAvailabilityState(checkbox);

    if (state.primaryAvailable) {
      return 'primary';
    }

    if (state.fallbackAvailable) {
      return 'fallback';
    }

    return 'primary';
  }

  getAddonSelectedSource(checkbox) {
    const requestedSource = checkbox?.dataset?.addonSelectedSource;
    const state = this.getAddonAvailabilityState(checkbox);

    if (requestedSource === 'fallback' && state.fallbackAvailable) {
      return 'fallback';
    }

    if (requestedSource === 'primary' && state.primaryAvailable) {
      return 'primary';
    }

    return this.getAddonDefaultSource(checkbox);
  }

  setAddonSelectedSource(checkbox, source) {
    if (!checkbox) return;

    const state = this.getAddonAvailabilityState(checkbox);
    let nextSource = source === 'fallback' ? 'fallback' : 'primary';

    if (nextSource === 'fallback' && !state.fallbackAvailable) {
      nextSource = this.getAddonDefaultSource(checkbox);
    } else if (nextSource === 'primary' && !state.primaryAvailable) {
      nextSource = this.getAddonDefaultSource(checkbox);
    }

    checkbox.dataset.addonSelectedSource = nextSource;
    this.syncAddonChoiceUI(checkbox);
    this.updateAddonCardPrice(checkbox);
  }

  getAddonSelectedVariantId(checkbox) {
    if (!checkbox) return '';

    return this.getAddonSelectedSource(checkbox) === 'fallback'
      ? checkbox.dataset.addonFallbackVariantId || ''
      : checkbox.dataset.addonVariantId || '';
  }

  getAddonSelectedPriceData(checkbox) {
    const source = this.getAddonSelectedSource(checkbox);
    const useFallback = source === 'fallback';
    const selectedTitle =
      (useFallback ? checkbox.dataset.addonFallbackTitle : checkbox.dataset.addonTitle) ||
      checkbox.dataset.addonTitle ||
      '';
    const displayTitle = checkbox.dataset.addonDisplayTitle || checkbox.dataset.addonTitle || selectedTitle;

    return {
      source,
      title: selectedTitle,
      displayTitle,
      conditionLabel: this.isAddonCustomerChoice(checkbox)
        ? useFallback
          ? checkbox.dataset.addonFallbackLabel || 'New'
          : checkbox.dataset.addonPrimaryLabel || 'Open Box'
        : '',
      price: this.parsePriceValue(
        useFallback ? checkbox.dataset.addonFallbackPrice : checkbox.dataset.addonPrice
      ),
      compareAtPrice: this.parsePriceValue(
        useFallback ? checkbox.dataset.addonFallbackCompareAtPrice : checkbox.dataset.addonCompareAtPrice
      ),
      displayAsPercentage: useFallback
        ? checkbox.dataset.addonFallbackDisplayDiscountAsPercentage === 'true'
        : checkbox.dataset.addonDisplayDiscountAsPercentage === 'true',
    };
  }

  syncAddonChoiceUI(checkbox) {
    const card = checkbox?.closest('.x3-configurator__addon-card');
    const group = card?.querySelector('[data-addon-choice-group]');
    if (!group) return;

    const state = this.getAddonAvailabilityState(checkbox);
    const showUnavailableChoices = this.isAddonCustomerChoiceOos(checkbox);
    const shouldShow =
      this.isAddonCustomerChoice(checkbox) &&
      (showUnavailableChoices ? state.available : state.primaryAvailable && state.fallbackAvailable);
    group.hidden = !shouldShow;
    group.classList.toggle('x3-configurator__addon-choice-group--show-unavailable', showUnavailableChoices);

    if (!shouldShow) {
      checkbox.dataset.addonSelectedSource = this.getAddonDefaultSource(checkbox);
      return;
    }

    const selectedSource = this.getAddonSelectedSource(checkbox);
    group.querySelectorAll('[data-addon-choice-source]').forEach((option) => {
      const source = option.dataset.addonChoiceSource;
      const available = source === 'fallback' ? state.fallbackAvailable : state.primaryAvailable;
      const selected = source === selectedSource;

      option.hidden = showUnavailableChoices ? false : !available;
      option.setAttribute('aria-checked', String(selected));
      option.setAttribute('aria-disabled', String(!available));
      option.setAttribute('tabindex', selected && available ? '0' : '-1');
      option.classList.toggle('x3-configurator__addon-choice-option--selected', selected);
      option.classList.toggle('x3-configurator__addon-choice-option--unavailable', !available);
      option.classList.toggle(
        'x3-configurator__addon-choice-option--visible-unavailable',
        showUnavailableChoices && !available
      );
    });
  }

  updateAddonCardPrice(checkbox) {
    const card = checkbox?.closest('.x3-configurator__addon-card');
    if (!card) return;

    const { displayTitle, price, compareAtPrice, displayAsPercentage } = this.getAddonSelectedPriceData(checkbox);
    const hasSale = this.hasValidCompareAtPrice(compareAtPrice, price);
    const titleElement = card.querySelector('.x3-configurator__addon-title');
    const priceElement = card.querySelector('.x3-configurator__addon-price-current');
    const priceContainer = card.querySelector('.x3-configurator__addon-price');
    let compareElement = card.querySelector('.x3-configurator__addon-price-compare');
    let savingsElement = card.querySelector('.x3-configurator__addon-savings-pill');

    if (titleElement && displayTitle) {
      titleElement.textContent = displayTitle;
    }

    if (priceElement) {
      priceElement.textContent = this.formatMoney(price);
    }

    if (!compareElement && priceContainer) {
      compareElement = document.createElement('span');
      compareElement.className = 'x3-configurator__addon-price-compare';
      priceContainer.appendChild(compareElement);
    }

    if (compareElement) {
      compareElement.textContent = hasSale ? this.formatMoney(compareAtPrice) : '';
      compareElement.hidden = !hasSale;
    }

    if (!savingsElement && priceContainer) {
      savingsElement = document.createElement('span');
      savingsElement.className = 'x3-configurator__savings-pill x3-configurator__addon-savings-pill';
      priceContainer.before(savingsElement);
    }

    if (savingsElement) {
      const savingsValue = Math.max(0, compareAtPrice - price);
      const savingsPercentage = hasSale ? Math.round((savingsValue * 100) / compareAtPrice) : 0;
      savingsElement.textContent = hasSale
        ? this.getSavingsPillText(savingsValue, savingsPercentage, displayAsPercentage)
        : '';
      savingsElement.hidden = !hasSale;
    }
  }

  getBeltButtonAvailabilityState(button) {
    if (!button) {
      return { available: false, primaryAvailable: false, fallbackAvailable: false };
    }

    const primaryId = button.dataset.beltNumericVariantId || button.dataset.beltVariantId || '';
    const fallbackId = button.dataset.beltFallbackNumericVariantId || button.dataset.beltFallbackVariantId || '';
    const primaryAvailable = this.getDatasetAvailability(button, 'beltAvailable', primaryId);
    const fallbackAvailable =
      button.dataset.beltFallbackEnabled === 'true' &&
      Boolean(fallbackId) &&
      this.getDatasetAvailability(button, 'beltFallbackAvailable', fallbackId);

    return {
      primaryAvailable,
      fallbackAvailable,
      available: primaryAvailable || fallbackAvailable,
    };
  }

  clearBeltSelection() {
    this.selectedBeltVariantId = null;
    this.selectedBeltSize = null;
    this.selectedBeltTitle = null;
    this.forceBeltButtons.forEach((button) => {
      button.classList.remove('x3-configurator__choice--selected');
      button.setAttribute('aria-checked', 'false');
    });
  }

  hasAnySellableForceBelt() {
    if (!this.isOpenBox) {
      return true;
    }

    return Array.from(this.forceBeltButtons).some((button) => this.getBeltButtonAvailabilityState(button).available);
  }

  hasValidForceBeltSelection() {
    if (!this.selectedBeltVariantId) {
      return false;
    }

    const selectedId = String(this.selectedBeltVariantId);
    const selectedNumericId = this.getNumericId(selectedId);

    return this.getForceBeltOptions().some((option) => {
      const optionId = String(option.variantId || '');
      const optionNumericId = String(option.numericVariantId || this.getNumericId(optionId));
      const fallbackId = String(option.fallbackVariantId || '');
      const fallbackNumericId = String(option.fallbackNumericVariantId || this.getNumericId(fallbackId));
      const referenceState = this.getReferenceAvailabilityState(option);
      const isPurchasable = referenceState.available || option.preorderEnabled === true;

      return (
        isPurchasable &&
        (selectedId === optionId ||
          selectedId === optionNumericId ||
          selectedNumericId === optionNumericId ||
          selectedId === fallbackId ||
          selectedId === fallbackNumericId ||
          selectedNumericId === fallbackNumericId)
      );
    });
  }

  resetForceSelections() {
    this.selectedBeltVariantId = null;
    this.selectedBeltSize = null;
    this.selectedBeltTitle = null;
    this.forcePremiumSelected = this.isForcePremiumConfigured();

    this.forceBeltButtons.forEach((button) => {
      button.classList.remove('x3-configurator__choice--selected');
      button.setAttribute('aria-checked', 'false');
    });

    if (this.forcePremiumToggle) {
      this.forcePremiumToggle.setAttribute('aria-checked', String(this.forcePremiumSelected));
    }

    this.updateForceBeltCallout();
  }

  clearPreviewImageTimer() {
    if (!this.previewImageTimer) {
      return;
    }

    window.clearTimeout(this.previewImageTimer);
    this.previewImageTimer = null;
  }

  schedulePreviewImage(callback, delay = PREVIEW_IMAGE_ENTER_DELAY_MS) {
    this.clearPreviewImageTimer();
    this.previewImageTimer = window.setTimeout(() => {
      this.previewImageTimer = null;
      callback();
    }, delay);
  }

  scheduleSelectedVariantPreview(delay = PREVIEW_IMAGE_RESTORE_DELAY_MS) {
    this.schedulePreviewImage(() => this.updatePreviewImage(), delay);
  }

  isPreviewInteractionActive(element) {
    return Boolean(
      element &&
        (element.matches(':hover') || element === document.activeElement || element.contains(document.activeElement))
    );
  }

  restoreSelectedVariantPreviewUnlessActive(element, activePreviewCallback) {
    if (this.isPreviewInteractionActive(element)) {
      this.clearPreviewImageTimer();
      activePreviewCallback();
      return;
    }

    this.updatePreviewImage();
  }

  handleChoicePreviewEnter(event) {
    const button = event.currentTarget;
    if (button.hidden || button.disabled || button.classList.contains('x3-configurator__choice--unavailable')) {
      return;
    }

    this.schedulePreviewImage(() => {
      this.updatePreviewImageForOption(button.dataset.step, button.dataset.optionValue);
    });
  }

  showChoicePreview(button) {
    if (!button || button.hidden || button.disabled || button.classList.contains('x3-configurator__choice--unavailable')) {
      return;
    }

    this.updatePreviewImageForOption(button.dataset.step, button.dataset.optionValue);
  }

  handleBeltPreviewEnter(event) {
    const button = event.currentTarget;
    if (button.hidden || button.disabled || button.classList.contains('x3-configurator__choice--unavailable')) {
      return;
    }

    const imageUrl = this.componentImages[button.dataset.beltNumericVariantId];
    if (imageUrl) {
      this.schedulePreviewImage(() => {
        this.crossfadeToImage(imageUrl, button.dataset.beltLabel || button.dataset.beltTitle || 'X3 Force squat belt');
      });
    }
  }

  showBeltPreview(button) {
    if (!button || button.hidden || button.disabled || button.classList.contains('x3-configurator__choice--unavailable')) {
      return;
    }

    const imageUrl = this.componentImages[button.dataset.beltNumericVariantId];
    if (imageUrl) {
      this.crossfadeToImage(imageUrl, button.dataset.beltLabel || button.dataset.beltTitle || 'X3 Force squat belt');
    }
  }

  handleAddonPreviewEnter(checkbox) {
    if (!checkbox || checkbox.disabled) {
      return;
    }

    const card = checkbox.closest('.x3-configurator__addon-card');
    if (card?.hidden || card?.classList.contains('x3-configurator__addon-card--unavailable')) {
      return;
    }

    if (checkbox.dataset.addonImage) {
      this.schedulePreviewImage(() => {
        this.crossfadeToImage(
          checkbox.dataset.addonImage,
          checkbox.dataset.addonDisplayTitle || checkbox.dataset.addonTitle || 'Configurator add-on'
        );
      });
    }
  }

  showAddonPreview(checkbox) {
    if (!checkbox || checkbox.disabled) {
      return;
    }

    const card = checkbox.closest('.x3-configurator__addon-card');
    if (card?.hidden || card?.classList.contains('x3-configurator__addon-card--unavailable')) {
      return;
    }

    if (checkbox.dataset.addonImage) {
      this.crossfadeToImage(
        checkbox.dataset.addonImage,
        checkbox.dataset.addonDisplayTitle || checkbox.dataset.addonTitle || 'Configurator add-on'
      );
    }
  }

  handleBeltChoiceClick(event) {
    const button = event.currentTarget;
    if (button.hidden || button.disabled || button.classList.contains('x3-configurator__choice--unavailable')) {
      return;
    }

    this.forceBeltButtons.forEach((btn) => {
      btn.classList.remove('x3-configurator__choice--selected');
      btn.setAttribute('aria-checked', 'false');
    });

    button.classList.add('x3-configurator__choice--selected');
    button.setAttribute('aria-checked', 'true');
    const beltState = this.getBeltButtonAvailabilityState(button);
    this.selectedBeltVariantId = beltState.primaryAvailable
      ? button.dataset.beltVariantId || null
      : beltState.fallbackAvailable
        ? button.dataset.beltFallbackVariantId || null
        : button.dataset.beltVariantId || button.dataset.beltFallbackVariantId || null;
    this.selectedBeltSize = this.decodeHtml(button.dataset.beltLabel || '') || null;
    this.selectedBeltTitle = this.decodeHtml(button.dataset.beltTitle || '') || null;

    this.hideError();
    this.updateUI();
    this.updateLayers();
    this.restoreSelectedVariantPreviewUnlessActive(button, () => this.showBeltPreview(button));
  }

  updateForceBeltCallout() {
    if (!this.forceBeltCallout) return;
    this.forceBeltCallout.hidden = !(this.isForceSelected() && !this.hasValidForceBeltSelection());
  }

  handleForcePremiumToggle() {
    if (!this.forcePremiumToggle) return;
    this.forcePremiumSelected = this.forcePremiumToggle.getAttribute('aria-checked') !== 'true';
    this.forcePremiumToggle.setAttribute('aria-checked', String(this.forcePremiumSelected));
    this.updateUI();
  }

  /**
   * Get the variant that matches current selections
   * Compares selections against each variant's options array
   */
  getSelectedVariant() {
    if (!this.hasCompleteOptionSet()) {
      return null;
    }

    return this.variants.find((variant) => {
      return this.options.every((option, index) => {
        return variant.options[index] === this.selections[option.name];
      });
    });
  }

  /**
   * Check if a specific option value has any available variant combinations
   * given the current selections for other options
   */
  isValueAvailable(optionName, value) {
    // Create a test selection with this value
    const testSelections = { ...this.selections, [optionName]: value };

    const matchingVariant = this.getMatchingVariantForSelections(testSelections);
    return this.isVariantPurchasableForSelections(matchingVariant, testSelections);
  }

  getMatchingVariantForSelections(selections) {
    return this.variants.find((variant) => {
      return this.options.every((option, index) => {
        return variant.options[index] === selections[option.name];
      });
    }) || null;
  }

  isVariantInStock(variant) {
    return Boolean(variant && variant.available && variant.componentsAvailable);
  }

  isOpenBoxSelectionPurchasable(variant, selections) {
    if (!this.isOpenBox || !variant?.available) {
      return false;
    }

    return this.options.every((option) => {
      const selectedValue = selections?.[option.name];
      if (!selectedValue) return false;

      return this.getReferenceAvailabilityState(option.valueMappings?.[selectedValue]).available;
    });
  }

  getOptionValuePreorder(optionName, value) {
    const option = this.options.find((entry) => entry.name === optionName);
    return option?.preorderData?.[value] || { enabled: false, preorderForced: false, info: '' };
  }

  getOptionValueComponentId(optionName, value) {
    const option = this.options.find((entry) => entry.name === optionName);
    const state = this.getReferenceAvailabilityState(option?.valueMappings?.[value]);
    return state.selectedNumericVariantId || state.numericVariantId;
  }

  isComponentInStock(componentId) {
    if (!componentId) {
      return true;
    }

    if (this.isOpenBox) {
      return this.componentAvailability[componentId] === true;
    }

    return this.componentAvailability[componentId] !== false;
  }

  getSelectedBeltOption() {
    if (!this.selectedBeltVariantId) {
      return null;
    }

    const selectedId = String(this.selectedBeltVariantId);
    const selectedNumericId = this.getNumericId(selectedId);

    return (
      this.getForceBeltOptions().find((option) => {
        const optionId = String(option.variantId || '');
        const optionNumericId = String(option.numericVariantId || this.getNumericId(optionId));
        const fallbackId = String(option.fallbackVariantId || '');
        const fallbackNumericId = String(option.fallbackNumericVariantId || this.getNumericId(fallbackId));

        return (
          selectedId === optionId ||
          selectedId === optionNumericId ||
          selectedNumericId === optionNumericId ||
          selectedId === fallbackId ||
          selectedId === fallbackNumericId ||
          selectedNumericId === fallbackNumericId
        );
      }) || null
    );
  }

  getPreorderEntriesForSelections(selections, variant = this.getMatchingVariantForSelections(selections)) {
    if (this.isOpenBox) return [];

    const generatedVariantUnavailable = Boolean(variant && !variant.available);
    const optionEntries = this.options
      .map((option) => {
        const selectedValue = selections[option.name];
        if (!selectedValue) return null;

        const preorderData = this.getOptionValuePreorder(option.name, selectedValue);
        if (!preorderData.enabled) return null;

        const componentId = this.getOptionValueComponentId(option.name, selectedValue);
        if (!preorderData.preorderForced && !generatedVariantUnavailable && this.isComponentInStock(componentId)) {
          return null;
        }

        return {
          optionName: option.name,
          value: selectedValue,
          label: `${option.name}: ${selectedValue}`,
          info: preorderData.info || '',
        };
      })
      .filter(Boolean);

    const selectedBeltOption = this.getSelectedBeltOption();
    const selectedBeltState = this.getReferenceAvailabilityState(selectedBeltOption);
    if (
      this.isForceSelected() &&
      selectedBeltOption &&
      selectedBeltOption.preorderEnabled === true &&
      (selectedBeltOption.preorderForced === true || selectedBeltState.available === false)
    ) {
      optionEntries.push({
        optionName: 'Squat Belt',
        value: this.selectedBeltSize || selectedBeltOption.label || 'Selected belt',
        label: `Squat Belt: ${this.selectedBeltSize || selectedBeltOption.label || 'Selected belt'}`,
        info: selectedBeltOption.preorderInfo || '',
      });
    }

    return optionEntries;
  }

  isVariantPreorderForSelections(variant, selections) {
    if (this.isOpenBox) return false;
    if (!variant) return false;
    return this.getPreorderEntriesForSelections(selections, variant).length > 0;
  }

  isVariantPurchasableForSelections(variant, selections) {
    if (!variant) return false;
    if (this.isOpenBox) return this.isOpenBoxSelectionPurchasable(variant, selections);
    if (this.isVariantInStock(variant)) return true;
    return this.isVariantPreorderForSelections(variant, selections);
  }

  getSelectedPreorderEntries() {
    return this.getPreorderEntriesForSelections(this.selections);
  }

  isSelectedVariantPreorder() {
    const variant = this.getSelectedVariant();
    return this.isVariantPreorderForSelections(variant, this.selections);
  }

  /**
   * Calculate and display price differences for each option value
   * Shows price relative to currently selected option in each step:
   * - Selected option: empty (no price shown)
   * - Cheaper options: -$XX
   * - More expensive options: +$XX
   */
  updatePriceDisplays() {
    const getPreferredVariant = (variants) => {
      return variants.find((variant) => variant.available && variant.componentsAvailable) || variants[0] || null;
    };

    const selectedVariantMatches = this.variants.filter((variant) => {
      return this.options.every((option, index) => {
        return variant.options[index] === this.selections[option.name];
      });
    });

    const selectedVariant = getPreferredVariant(selectedVariantMatches);

    if (!selectedVariant) {
      this.options.forEach((option) => {
        option.values.forEach((value) => {
          const key = `${option.name}-${value}`;
          this.updatePriceDisplay(key, 0);
        });
      });
      return;
    }

    this.options.forEach((option, optionIndex) => {
      option.values.forEach((value) => {
        const matchingVariants = this.variants.filter((variant) => {
          return this.options.every((compareOption, compareIndex) => {
            if (compareIndex === optionIndex) {
              return variant.options[compareIndex] === value;
            }
            return variant.options[compareIndex] === this.selections[compareOption.name];
          });
        });

        const valueVariant = getPreferredVariant(matchingVariants);
        const priceDiff = valueVariant ? valueVariant.price - selectedVariant.price : 0;

        const key = `${option.name}-${value}`;
        this.updatePriceDisplay(key, priceDiff);
      });
    });
  }

  updatePriceDisplay(key, priceDiff) {
    const deltaElement = this.section.querySelector(`[data-price-display="${key}"]`);
    if (!deltaElement) return;

    // Get the choice button to check if it's base/upgrade and selected/not selected
    const choiceButton = this.section.querySelector(
      `[data-price-display="${key}"]`
    )?.closest('.x3-configurator__choice');
    
    if (!choiceButton) return;

    const optionName = choiceButton.dataset.step;
    const optionValue = choiceButton.dataset.optionValue;
    const isBase = choiceButton.dataset.priceIsBase === 'true';
    const isSelected = this.selections[optionName] === optionValue;
    const visibleChoiceCount = Array.from(
      choiceButton.closest('.x3-configurator__choices')?.querySelectorAll('.x3-configurator__choice') || []
    ).filter((choice) => !choice.hidden).length;

    // Remove all modifier classes
    deltaElement.classList.remove('x3-configurator__choice-delta--included', 'x3-configurator__choice-delta--upgrade');

    if (isSelected && visibleChoiceCount === 1) {
      deltaElement.textContent = '';
      return;
    }

    // Four state delta pricing logic
    if (isBase && isSelected) {
      // State 1: Base/standard option, currently selected → Show "Included"
      deltaElement.textContent = 'Included';
      deltaElement.classList.add('x3-configurator__choice-delta--included');
    } else if (!isBase && !isSelected) {
      // State 2: Upgrade option, NOT currently selected → Show "+ $X"
      deltaElement.textContent = '+ ' + this.formatMoney(priceDiff);
      deltaElement.classList.add('x3-configurator__choice-delta--upgrade');
    } else if (isBase && !isSelected) {
      // State 3: Base/standard option, NOT selected (upgrade is selected) → Show "– $X"
      // Use en-dash (–) not hyphen (-), space after dash
      deltaElement.textContent = '– ' + this.formatMoney(Math.abs(priceDiff));
      deltaElement.classList.add('x3-configurator__choice-delta--upgrade');
    } else {
      // State 4: Upgrade option, currently selected → Show nothing (space reserved)
      deltaElement.textContent = '';
      deltaElement.classList.add('x3-configurator__choice-delta--upgrade');
    }
  }

  /**
   * Update availability state for all option values
   * Disables buttons for unavailable combinations
   */
  updateAvailability(settled = false) {
    let selectionChanged = false;

    this.options.forEach((option) => {
      const choiceStates = option.values
        .map((value) => {
          const button = this.section.querySelector(
            `[data-step="${option.name}"][data-option-value="${value}"]`
          );
          if (!button) return null;

          const testSelections = { ...this.selections, [option.name]: value };
          const matchingVariant = this.getMatchingVariantForSelections(testSelections);
          const isAvailable = this.isVariantPurchasableForSelections(matchingVariant, testSelections);
          const isChoicePreorderEnabled = this.getOptionValuePreorder(option.name, value).enabled;
          const isPreorder =
            !this.isOpenBox &&
            isChoicePreorderEnabled &&
            this.isVariantPreorderForSelections(matchingVariant, testSelections);

          return {
            value,
            button,
            isAvailable,
            isPreorder,
            hidden: false,
          };
        })
        .filter(Boolean);

      if (this.isOpenBox) {
        choiceStates.forEach((state) => {
          state.hidden = !state.isAvailable;
        });
      }

      const selectedState = choiceStates.find((state) => state.value === this.selections[option.name]);
      if (selectedState?.hidden) {
        const replacement = choiceStates.find((state) => !state.hidden && state.isAvailable);
        if (replacement) {
          this.selections[option.name] = replacement.value;
          this.clearAddonsForOption(option.name);
          selectionChanged = true;
        }
      }

      if (this.isOpenBox && choiceStates.length > 0) {
        const step = choiceStates[0].button.closest('.x3-configurator__step');
        if (step) {
          step.hidden = !choiceStates.some((state) => !state.hidden);
        }
      }

      choiceStates.forEach((state) => {
        const { button, isAvailable, isPreorder, hidden } = state;
        const preorderElement = button.querySelector('[data-choice-preorder]');
        const statusElement = button.querySelector('.x3-configurator__choice-status');
        if (statusElement && !statusElement.dataset.defaultText) {
          statusElement.dataset.defaultText = statusElement.textContent.trim() || '(Out of Stock)';
        }

        button.hidden = hidden;
        if (isAvailable) {
          button.classList.remove('x3-configurator__choice--unavailable');
          button.classList.toggle('x3-configurator__choice--preorder', isPreorder);
          button.disabled = false;
        } else {
          button.classList.add('x3-configurator__choice--unavailable');
          button.classList.remove('x3-configurator__choice--preorder');
          button.disabled = true;
        }

        if (preorderElement) {
          preorderElement.hidden = !isPreorder;
        }

        if (statusElement?.dataset.defaultText) {
          statusElement.textContent = statusElement.dataset.defaultText;
        }
      });
    });

    if (selectionChanged) {
      if (!settled) {
        const settledSelectionChanged = this.updateAvailability(true);
        if (settledSelectionChanged) {
          return true;
        }
      }

      this.updatePriceDisplays();
      this.updateAddonSections();
      this.updateUI();
      return true;
    }

    return false;
  }

  handleChoiceClick(event) {
    const button = event.currentTarget;
    const optionName = button.dataset.step;
    const optionValue = button.dataset.optionValue;

    // Don't do anything if unavailable
    if (button.hidden || button.disabled || button.classList.contains('x3-configurator__choice--unavailable')) {
      return;
    }

    // If already selected, keep the hover/focus preview active until the user leaves it.
    if (button.classList.contains('x3-configurator__choice--selected')) {
      this.restoreSelectedVariantPreviewUnlessActive(button, () => this.showChoicePreview(button));
      return;
    }

    // Find the parent choices container
    const choicesContainer = button.closest('.x3-configurator__choices');
    if (!choicesContainer) return;

    // Deselect all in this option group
    choicesContainer.querySelectorAll('.x3-configurator__choice').forEach((btn) => {
      btn.classList.remove('x3-configurator__choice--selected');
      btn.setAttribute('aria-checked', 'false');
    });

    // Select this one
    button.classList.add('x3-configurator__choice--selected');
    button.setAttribute('aria-checked', 'true');

    // Update selection state
    this.selections[optionName] = optionValue;
    if (this.preorderCheckbox) {
      this.preorderCheckbox.checked = false;
    }
    this.preorderAccepted = false;

    // Clear any selected add-ons for this option (they're value-specific)
    this.clearAddonsForOption(optionName);

    // Update availability for other options based on new selection
    this.updateAvailability();

    // Update add-on step visibility for this option
    this.updateAddonSections();

    // Update price displays relative to new selection
    this.updatePriceDisplays();

    // Update UI
    this.updateUI();
    this.restoreSelectedVariantPreviewUnlessActive(button, () => this.showChoicePreview(button));

    // Clear any previous errors
    this.hideError();
  }

  formatMoney(cents) {
    const amount = (cents / 100).toFixed(2);
    // Remove trailing zeros for whole dollar amounts
    const formatted = amount.replace(/\.00$/, '');
    return '$' + formatted.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  updateUI() {
    this.updateOpenBoxAddonAvailability();
    this.syncSelectedChoiceButtons();
    this.updateForceUI();
    this.updateSummary();
    this.updateAddonSummary();
    this.updateForceSummary();
    this.updateTotalPrice();
    this.updatePreorderUI();
    this.updateAddToCartButton();
    this.updateStepNumbers();
  }

  syncSelectedChoiceButtons() {
    this.choiceButtons.forEach((button) => {
      const optionName = button.dataset.step;
      const optionValue = button.dataset.optionValue;
      if (!optionName) return;

      const isSelected = this.selections[optionName] === optionValue;
      button.classList.toggle('x3-configurator__choice--selected', isSelected);
      button.setAttribute('aria-checked', String(isSelected));
    });
  }

  updateSummary() {
    // Update summary for each option dynamically
    this.options.forEach((option) => {
      const summaryElement = this.section.querySelector(
        `[data-summary="${option.name}"] [data-summary-value]`
      );
      if (summaryElement) {
        summaryElement.textContent = (this.selections[option.name] || '')
          .replace(' Bands', '')
          .replace(' Ground Plate', '');
      }
    });
  }

  updateForceUI() {
    const isForce = this.isForceSelected();

    if (!isForce) {
      if (this.selectedBeltVariantId || this.selectedBeltSize) {
        this.resetForceSelections();
      }

      if (this.forceBeltStep) {
        this.forceBeltStep.hidden = true;
      }
      if (this.forcePremiumStep) {
        this.forcePremiumStep.hidden = true;
      }
      this.updateForceBeltCallout();
      return;
    }

    if (this.forceBeltStep && this.getForceBeltOptions().length > 0) {
      this.forceBeltStep.hidden = false;
    }

    this.updateOpenBoxForceBeltVisibility();

    if (this.forcePremiumStep) {
      this.forcePremiumStep.hidden = !this.isForcePremiumConfigured();
    }

    this.updateForceBeltCallout();
  }

  updateOpenBoxForceBeltVisibility() {
    if (!this.isOpenBox || !this.forceBeltButtons.length) {
      return;
    }

    const beltStates = Array.from(this.forceBeltButtons).map((button) => ({
      button,
      ...this.getBeltButtonAvailabilityState(button),
    }));
    let selectedBeltUnavailable = false;

    beltStates.forEach(({ button, available }) => {
      button.hidden = false;
      button.disabled = !available;
      button.classList.toggle('x3-configurator__choice--unavailable', !available);

      if (button.classList.contains('x3-configurator__choice--selected') && !available) {
        selectedBeltUnavailable = true;
      }
    });

    if (selectedBeltUnavailable) {
      this.clearBeltSelection();
    }

    if (this.forceBeltStep) {
      this.forceBeltStep.hidden = beltStates.length === 0;
    }
  }

  updateForceSummary() {
    const isForce = this.isForceSelected();

    if (this.forceBeltSummary && this.forceBeltSummaryValue) {
      const showBelt = isForce && Boolean(this.selectedBeltSize);
      this.forceBeltSummary.hidden = !showBelt;
      this.forceBeltSummaryValue.textContent = showBelt ? this.selectedBeltSize : '';
    }

    if (this.forcePremiumSummary) {
      this.forcePremiumSummary.hidden = !(isForce && this.forcePremiumSelected && this.isForcePremiumConfigured());
    }
  }

  updateStepNumbers() {
    const visibleSteps = Array.from(this.section.querySelectorAll('.x3-configurator__step')).filter(
      (step) => !step.hidden
    );

    visibleSteps.forEach((step, index) => {
      const number = step.querySelector('.x3-configurator__step-number');
      if (number) {
        number.textContent = String(index + 1);
      }
    });
  }

  updateTotalPrice() {
    if (!this.totalPrice) return;

    const variant = this.getSelectedVariant();
    if (variant) {
      const addonTotal = this.calculateAddonTotal();
      const variantPrice = this.parsePriceValue(variant.price);
      const currentTotal = variantPrice + addonTotal;
      this.totalPrice.textContent = this.formatMoney(currentTotal);

      if (this.totalComparePrice) {
        const variantCompareAtPrice = this.getEffectiveCompareAtPrice(variant.compareAtPrice, variantPrice);
        const addonCompareAtTotal = this.calculateAddonCompareAtTotal();
        const compareTotal = variantCompareAtPrice + addonCompareAtTotal;
        const hasSale = compareTotal > currentTotal;

        this.totalComparePrice.hidden = !hasSale;
        this.totalComparePrice.textContent = hasSale ? this.formatMoney(compareTotal) : '';

        if (this.totalSavingsPill) {
          const totalSavingsValue = compareTotal - currentTotal;
          this.totalSavingsPill.hidden = !hasSale;
          this.totalSavingsPill.textContent = hasSale
            ? this.getSavingsPillText(totalSavingsValue, 0, false)
            : '';
        }
      }
    } else {
      if (this.totalComparePrice) {
        this.totalComparePrice.hidden = true;
        this.totalComparePrice.textContent = '';
      }

      if (this.totalSavingsPill) {
        this.totalSavingsPill.hidden = true;
        this.totalSavingsPill.textContent = '';
      }
    }
  }

  updateAddToCartButton() {
    if (!this.addToCartBtn || !this.addToCartBtnText) return;

    const variant = this.getSelectedVariant();
    if (variant) {
      this.addToCartBtn.dataset.variantId = variant.id;

      const isPreorder = this.isSelectedVariantPreorder();
      const hasSellableRequiredBelt = !this.isOpenBox || !this.isForceSelected() || this.hasAnySellableForceBelt();
      const isPurchasable =
        this.isVariantPurchasableForSelections(variant, this.selections) && hasSellableRequiredBelt;
      const requiresBeltSelection = hasSellableRequiredBelt && this.isForceSelected() && !this.hasValidForceBeltSelection();
      const requiresOpenBoxAcknowledgement =
        this.isOpenBoxAcknowledgementRequired() && !this.openBoxAcknowledgementAccepted;
      this.addToCartBtn.disabled =
        !isPurchasable || (isPreorder && !this.preorderAccepted) || requiresOpenBoxAcknowledgement;
      this.addToCartBtn.classList.toggle('x3-configurator__add-to-cart--blocked', requiresBeltSelection);

      const originalText = this.addToCartBtn.dataset.originalText || this.addToCartBtnText.textContent.trim() || 'Add to Cart';
      if (!this.addToCartBtn.dataset.originalText) {
        this.addToCartBtn.dataset.originalText = originalText;
      }

      if (!isPurchasable) {
        this.addToCartBtnText.textContent = 'Sold Out';
      } else if (requiresBeltSelection) {
        this.addToCartBtnText.textContent = 'Choose Belt Size';
      } else if (isPreorder) {
        this.addToCartBtnText.textContent = this.preorderSettings.buttonText || 'Preorder';
      } else {
        this.addToCartBtnText.textContent = this.cartItemKey ? 'Update Cart' : originalText;
      }
    } else {
      this.addToCartBtn.disabled = true;
      this.addToCartBtn.classList.remove('x3-configurator__add-to-cart--blocked');
      this.addToCartBtnText.textContent = 'Sold Out';
    }
  }

  scrollToForceBeltStep() {
    if (!this.forceBeltStep) {
      return;
    }

    this.forceBeltStep.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    });

    const firstAvailableBelt = Array.from(this.forceBeltButtons).find(
      (button) => !button.disabled && !button.classList.contains('x3-configurator__choice--unavailable')
    );

    window.setTimeout(() => {
      firstAvailableBelt?.focus({ preventScroll: true });
    }, 350);
  }

  updatePreorderUI() {
    const preorderEntries = this.getSelectedPreorderEntries();
    const isPreorder = this.isSelectedVariantPreorder();

    if (!this.preorderContainer || !this.preorderItems || !this.preorderCheckbox) {
      return;
    }

    if (this.isOpenBox || !isPreorder || preorderEntries.length === 0) {
      this.preorderContainer.hidden = true;
      this.preorderCheckbox.checked = false;
      this.preorderAccepted = false;
      this.preorderItems.innerHTML = '';
      return;
    }

    this.preorderContainer.hidden = false;
    this.preorderItems.innerHTML = preorderEntries
      .map(
        (entry) => `
          <div class="x3-configurator__preorder-item">
            <span class="x3-configurator__preorder-item-label">${this.escapeHtml(entry.label)}</span>
            ${
              entry.info
                ? `<span class="x3-configurator__preorder-item-info">${this.escapeHtml(entry.info)}</span>`
                : ''
            }
          </div>
        `
      )
      .join('');
  }

  escapeHtml(value) {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  decodeHtml(value) {
    if (!value) {
      return '';
    }

    const textarea = document.createElement('textarea');
    textarea.innerHTML = value;
    return textarea.value;
  }

  renderRichTextDescriptions() {
    this.section.querySelectorAll('[data-configurator-rich-text]').forEach((element) => {
      const rawValue = this.getRichTextValue(element);
      if (!rawValue.trim()) {
        element.hidden = true;
        element.innerHTML = '';
        return;
      }

      element.hidden = false;
      element.innerHTML = this.formatRichText(rawValue, element.dataset.richTextMode || 'block');
    });
  }

  getRichTextValue(element) {
    const encodedValue = element.getAttribute('data-rich-text-json');
    if (!encodedValue) {
      return '';
    }

    try {
      const parsed = JSON.parse(encodedValue);
      return typeof parsed === 'string' ? parsed : '';
    } catch (_) {
      return encodedValue;
    }
  }

  formatRichText(value, mode) {
    const normalizedValue = String(value || '').replace(/\r\n?/g, '\n');

    if (!this.hasHtmlSyntax(normalizedValue)) {
      return mode === 'inline'
        ? this.formatPlainInlineText(normalizedValue)
        : this.formatPlainBlockText(normalizedValue);
    }

    return this.sanitizeRichTextHtml(normalizedValue, mode);
  }

  hasHtmlSyntax(value) {
    return /<\s*\/?\s*[a-z][^>]*>/i.test(value);
  }

  formatPlainInlineText(value) {
    return this.escapeHtml(value.trim()).replace(/\n/g, '<br>');
  }

  formatPlainBlockText(value) {
    const paragraphs = value
      .trim()
      .split(/\n{2,}/)
      .map((paragraph) => paragraph.trim())
      .filter(Boolean);

    return paragraphs
      .map((paragraph) => `<p>${this.escapeHtml(paragraph).replace(/\n/g, '<br>')}</p>`)
      .join('');
  }

  sanitizeRichTextHtml(value, mode) {
    const template = document.createElement('template');
    template.innerHTML = value;

    const container = document.createElement('div');
    Array.from(template.content.childNodes).forEach((node) => {
      container.appendChild(this.sanitizeRichTextNode(node, mode));
    });

    return container.innerHTML.trim();
  }

  sanitizeRichTextNode(node, mode) {
    if (node.nodeType === Node.TEXT_NODE) {
      return document.createTextNode(node.textContent || '');
    }

    if (node.nodeType !== Node.ELEMENT_NODE) {
      return document.createDocumentFragment();
    }

    const tagName = node.tagName.toUpperCase();
    if (RICH_TEXT_DROP_TAGS.has(tagName)) {
      return document.createDocumentFragment();
    }

    const allowedTags = mode === 'inline' ? RICH_TEXT_INLINE_TAGS : RICH_TEXT_BLOCK_TAGS;
    if (!allowedTags.has(tagName)) {
      const fragment = document.createDocumentFragment();
      Array.from(node.childNodes).forEach((childNode) => {
        fragment.appendChild(this.sanitizeRichTextNode(childNode, mode));
      });

      if (mode === 'inline' && RICH_TEXT_INLINE_BREAK_TAGS.has(tagName)) {
        fragment.appendChild(document.createElement('br'));
      }

      return fragment;
    }

    const element = document.createElement(tagName.toLowerCase());
    if (tagName === 'A') {
      const href = this.getSafeRichTextHref(node.getAttribute('href'));
      if (!href) {
        const fragment = document.createDocumentFragment();
        Array.from(node.childNodes).forEach((childNode) => {
          fragment.appendChild(this.sanitizeRichTextNode(childNode, mode));
        });
        return fragment;
      }

      element.setAttribute('href', href);
      if (node.getAttribute('target') === '_blank') {
        element.setAttribute('target', '_blank');
        element.setAttribute('rel', 'noopener noreferrer');
      }
    }

    Array.from(node.childNodes).forEach((childNode) => {
      element.appendChild(this.sanitizeRichTextNode(childNode, mode));
    });

    return element;
  }

  getSafeRichTextHref(href) {
    const trimmedHref = String(href || '').trim();
    if (!trimmedHref) {
      return '';
    }

    try {
      const url = new URL(trimmedHref, window.location.origin);
      return ['http:', 'https:', 'mailto:', 'tel:'].includes(url.protocol) ? trimmedHref : '';
    } catch (_) {
      return '';
    }
  }

  updatePreviewImage() {
    if (!this.previewImage) return;
    this.clearPreviewImageTimer();

    // When layers are active, update layer visibility instead of crossfading.
    // Hover callbacks (updatePreviewImageForOption → updatePreviewImage) also
    // route through here and are intentionally suppressed by this early return.
    if (this.layersActive) {
      this.updateLayers();
      return;
    }

    const variant = this.getSelectedVariant();
    if (variant && variant.image) {
      this.crossfadeToImage(variant.image, variant.title);
    }
  }

  /**
   * Apply selections from a known variant ID, used when opening the configurator
   * in cart-edit mode so the user sees their current choices pre-selected.
   *
   * @param {string|number} variantId
   */
  applyVariantSelections(variantId) {
    const id = parseInt(variantId, 10);
    const variant = this.variants.find((v) => v.id === id);
    if (!variant) return;
    this.options.forEach((option, index) => {
      if (variant.options[index] !== undefined) {
        this.selections[option.name] = variant.options[index];
      }
    });
    this.updateUI();
    this.updatePriceDisplays();
    this.updateAvailability();
    this.updatePreviewImage();
  }

  /**
   * Programmatically select a Force belt button matching the given variant ID (GID or numeric).
   * Mirrors the state changes that handleBeltChoiceClick would perform.
   *
   * @param {string} beltVariantId - GID or numeric variant ID stored in the cart property
   */
  applyBeltSelection(beltVariantId) {
    if (!beltVariantId || !this.forceBeltButtons.length) return;

    const normalizedId = String(beltVariantId);
    const numericId = this.getNumericId(normalizedId);

    const match = Array.from(this.forceBeltButtons).find((btn) => {
      const btnId = String(btn.dataset.beltVariantId || '');
      const btnNumericId = String(btn.dataset.beltNumericVariantId || this.getNumericId(btnId));
      const fallbackId = String(btn.dataset.beltFallbackVariantId || '');
      const fallbackNumericId = String(btn.dataset.beltFallbackNumericVariantId || this.getNumericId(fallbackId));
      return (
        btnId === normalizedId ||
        btnNumericId === numericId ||
        fallbackId === normalizedId ||
        fallbackNumericId === numericId
      );
    });

    if (!match) return;

    this.forceBeltButtons.forEach((btn) => {
      btn.classList.remove('x3-configurator__choice--selected');
      btn.setAttribute('aria-checked', 'false');
    });

    match.classList.add('x3-configurator__choice--selected');
    match.setAttribute('aria-checked', 'true');
    this.selectedBeltVariantId = normalizedId || match.dataset.beltVariantId || null;
    this.selectedBeltSize = this.decodeHtml(match.dataset.beltLabel || '') || null;
    this.selectedBeltTitle = this.decodeHtml(match.dataset.beltTitle || '') || null;

    this.updateUI();
    this.updateLayers();
  }

  /**
   * Crossfade to a new image without showing background.
   * Uses two stacked image layers - loads new image into the hidden layer,
   * then swaps visibility so the transition is seamless.
   *
   * @param {string} newSrc - URL of the new image
   * @param {string} newAlt - Alt text for the new image
   */
  crossfadeToImage(newSrc, newAlt) {
    if (!this.imageLayerA || !this.imageLayerB) return;

    // Determine which layer is currently active and which is incoming
    const activeImg = this.activeLayer === 'a' ? this.imageLayerA : this.imageLayerB;
    const incomingImg = this.activeLayer === 'a' ? this.imageLayerB : this.imageLayerA;
    const nextSrc = new URL(newSrc, window.location.href).href;

    // If the new src is the same as active, no need to transition
    if (activeImg.src === nextSrc) return;

    const swapId = this.previewImageSwapId + 1;
    this.previewImageSwapId = swapId;

    incomingImg.onload = null;
    incomingImg.onerror = null;
    incomingImg.classList.remove('x3-configurator__image--active');
    incomingImg.classList.add('x3-configurator__image--incoming');

    // Load the new image into the incoming (hidden) layer
    incomingImg.src = nextSrc;
    incomingImg.alt = newAlt;

    // Wait for the image to load before starting the crossfade
    // (For cached/preloaded images this is nearly instant)
    const startCrossfade = () => {
      if (swapId !== this.previewImageSwapId) {
        return;
      }

      // Give the browser a committed hidden state before applying the active class.
      incomingImg.getBoundingClientRect();
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          if (swapId !== this.previewImageSwapId) {
            return;
          }

          activeImg.classList.remove('x3-configurator__image--active');
          activeImg.classList.add('x3-configurator__image--incoming');
          incomingImg.classList.remove('x3-configurator__image--incoming');
          incomingImg.classList.add('x3-configurator__image--active');

          this.activeLayer = this.activeLayer === 'a' ? 'b' : 'a';
        });
      });
    };

    // If image is already loaded (cached), start immediately
    if (incomingImg.complete && incomingImg.naturalWidth > 0) {
      startCrossfade();
    } else {
      // Wait for load, then crossfade
      incomingImg.onload = startCrossfade;
      // Handle error gracefully - still do the swap to avoid stuck state
      incomingImg.onerror = startCrossfade;
    }
  }

  /**
   * Temporarily update preview image based on a hovered/focused option value.
   * Looks up the component variant's image from valueMappings -> componentImages.
   * Falls back to variant image if no component image exists.
   *
   * @param {string} optionName - The option name (e.g., "Bar", "Bands")
   * @param {string} optionValue - The selected value (e.g., "X3 Elite", "Original")
   */
  updatePreviewImageForOption(optionName, optionValue) {
    if (!this.previewImage) return;

    // Find the option config
    const option = this.options.find((opt) => opt.name === optionName);
    if (!option || !option.valueMappings) {
      // No mappings, fall back to variant image
      this.updatePreviewImage();
      return;
    }

    // Get the component variant GID for this value
    const referenceState = this.getReferenceAvailabilityState(option.valueMappings[optionValue]);
    const variantId = referenceState.selectedNumericVariantId || referenceState.numericVariantId;
    if (!variantId) {
      // No mapping for this value, fall back to variant image
      this.updatePreviewImage();
      return;
    }

    // Look up the image URL from componentImages using the numeric ID
    const imageUrl = this.componentImages[variantId];

    // If no component image, fall back to variant image
    if (!imageUrl) {
      this.updatePreviewImage();
      return;
    }

    // Use crossfade for smooth transition
    this.crossfadeToImage(imageUrl, optionValue);
  }

  getCartSectionIds() {
    return Array.from(document.querySelectorAll('cart-items-component'))
      .map((item) => item.dataset?.sectionId)
      .filter(Boolean);
  }

  getSelectedOptionFallbackRequests() {
    return this.options
      .map((option) => {
        const selectedValue = this.selections[option.name];
        const reference = this.normalizeVariantReference(option.valueMappings?.[selectedValue]);
        if (!reference.variantId || reference.fallbackEnabled !== true) {
          return null;
        }

        return {
          key: `option:${option.name}`,
          variantId: reference.variantId,
          fallbackEnabled: reference.fallbackEnabled,
          fallbackVariantId: reference.fallbackVariantId,
          fallbackMode: reference.fallbackMode,
        };
      })
      .filter(Boolean);
  }

  getAddonResolutionKey(checkbox) {
    return `addon:${checkbox.dataset.addonOption || ''}:${checkbox.dataset.addonVariantId || ''}`;
  }

  getSelectedAddonFallbackRequests() {
    return Array.from(this.addonCheckboxes)
      .filter((checkbox) => checkbox.checked)
      .filter((checkbox) => checkbox.dataset.addonFallbackEnabled === 'true')
      .map((checkbox) => ({
        key: this.getAddonResolutionKey(checkbox),
        variantId: checkbox.dataset.addonVariantId || '',
        fallbackEnabled: checkbox.dataset.addonFallbackEnabled === 'true',
        fallbackVariantId: checkbox.dataset.addonFallbackVariantId || '',
        fallbackMode: checkbox.dataset.addonFallbackMode || 'silent_auto',
        selectedVariantId:
          checkbox.dataset.addonFallbackMode === 'customer_choice' ||
          checkbox.dataset.addonFallbackMode === 'customer_choice_oos'
            ? this.getAddonSelectedVariantId(checkbox)
            : '',
      }))
      .filter((request) => request.variantId);
  }

  getForceBeltFallbackRequest() {
    const selectedBeltOption = this.getSelectedBeltOption();
    if (
      !this.isForceSelected() ||
      !selectedBeltOption?.variantId ||
      selectedBeltOption.fallbackEnabled !== true
    ) {
      return null;
    }

    const reference = this.normalizeVariantReference(selectedBeltOption);
    return {
      key: 'force-belt',
      variantId: reference.variantId,
      fallbackEnabled: reference.fallbackEnabled,
      fallbackVariantId: reference.fallbackVariantId,
      fallbackMode: reference.fallbackMode,
    };
  }

  async resolveFallbacksBeforeCart() {
    const requests = [
      ...this.getSelectedOptionFallbackRequests(),
      ...this.getSelectedAddonFallbackRequests(),
    ];
    const forceBeltRequest = this.getForceBeltFallbackRequest();
    if (forceBeltRequest) {
      requests.push(forceBeltRequest);
    }

    if (requests.length === 0) {
      return new Map();
    }

    const response = await fetchWithTimeout(
      FALLBACK_RESOLVE_URL,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ fallbacks: requests }),
      },
      PREORDER_PROXY_TIMEOUT_MS
    );
    const payload = await response.json().catch(() => ({}));

    if (!response.ok) {
      throw new Error(payload.error || 'Failed to verify product availability');
    }

    const results = Array.isArray(payload.results) ? payload.results : [];
    const resultMap = new Map(results.map((result) => [result.key, result]));
    const unavailable = requests.filter((request) => resultMap.get(request.key)?.available !== true);
    if (unavailable.length > 0) {
      throw new Error('One or more selected products are no longer available.');
    }

    return resultMap;
  }

  getPreorderLineProperties() {
    if (this.isOpenBox || !this.isSelectedVariantPreorder()) {
      return {};
    }

    const preorderEntries = this.getSelectedPreorderEntries();

    return {
      _x3_configurator_preorder: 'true',
      _x3_configurator_preorder_title: this.preorderSettings.title || 'Preorder details',
      _x3_configurator_preorder_checkbox_text:
        this.preorderSettings.checkboxText ||
        'I understand these items are preorders and will ship when available.',
      _x3_configurator_preorder_info: JSON.stringify(preorderEntries.map((entry) => entry.info)),
      _x3_configurator_preorder_label: JSON.stringify(preorderEntries.map((entry) => entry.label)),
    };
  }

  isOpenBoxAcknowledgementRequired() {
    return (
      this.isOpenBox &&
      this.openBoxSettings.acknowledgementEnabled === true &&
      Boolean(this.openBoxAcknowledgementCheckbox)
    );
  }

  getOpenBoxLineProperties() {
    if (!this.isOpenBox) {
      return {};
    }

    const properties = {
      _x3_open_box: 'true',
      _x3_open_box_identifier: 'x3-open-box-configurator',
    };

    if (this.openBoxAcknowledgementAccepted) {
      properties._x3_open_box_acknowledged = 'true';
    }

    return properties;
  }

  getAddonOpenBoxLineProperties(fallbackResolution) {
    if (!this.isOpenBox || fallbackResolution?.source === 'fallback') {
      return {};
    }

    return this.getOpenBoxLineProperties();
  }

  getForceLineProperties(fallbackResolutions = new Map()) {
    if (!this.isForceSelected()) {
      return {};
    }

    const resolvedBeltVariantId =
      fallbackResolutions.get('force-belt')?.selectedVariantId || this.selectedBeltVariantId;

    return {
      _x3_force: 'true',
      _x3_belt_variant_id: resolvedBeltVariantId,
      _x3_belt_size: this.selectedBeltSize || '',
      _x3_belt_title: this.selectedBeltTitle || '',
    };
  }

  getMainLineItem(variant, fallbackResolutions = new Map(), configurationId = '') {
    const properties = {
      ...this.getConfigurationLineProperties(configurationId),
      ...this.getPreorderLineProperties(),
      ...this.getOpenBoxLineProperties(),
      ...this.getForceLineProperties(fallbackResolutions),
    };
    const lineItem = {
      id: variant.id,
      quantity: 1,
    };

    if (Object.keys(properties).length > 0) {
      lineItem.properties = properties;
    }

    return lineItem;
  }

  getAddonLineItems(fallbackResolutions = new Map(), configurationId = '') {
    const items = [];
    const configurationProperties = this.getConfigurationLineProperties(configurationId);

    this.addonCheckboxes.forEach((checkbox) => {
      if (!checkbox.checked) return;
      const fallbackResolution = fallbackResolutions.get(this.getAddonResolutionKey(checkbox));
      const resolvedVariantId =
        fallbackResolution?.selectedVariantId ||
        this.getAddonSelectedVariantId(checkbox);
      const variantId = this.parseNumericId(resolvedVariantId);
      if (variantId) {
        const lineItem = { id: variantId, quantity: 1 };
        const properties = {
          ...configurationProperties,
          ...this.getAddonOpenBoxLineProperties(fallbackResolution),
        };
        if (Object.keys(properties).length > 0) {
          lineItem.properties = properties;
        }
        items.push(lineItem);
      }
    });
    return items;
  }

  buildCartItems(variant, fallbackResolutions = new Map()) {
    const items = [];
    const configurationId = this.getOrCreateConfigurationId();
    const forcePremiumLine = this.getForcePremiumLineItem(configurationId);

    if (forcePremiumLine) {
      items.push(forcePremiumLine);
    }

    items.push(
      this.getMainLineItem(variant, fallbackResolutions, configurationId),
      ...this.getAddonLineItems(fallbackResolutions, configurationId)
    );

    return items;
  }

  getCartItemsQuantity(items) {
    return items.reduce((quantity, item) => quantity + (item.quantity || 0), 0);
  }

  async addItemsToCart(items, sectionIds) {
    const response = await fetch(CART_ADD_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        items,
        sections: sectionIds.join(','),
      }),
    });
    let result = {};

    try {
      result = await response.json();
    } catch (_) {
      // Shopify normally returns JSON here. Keep the fallback error generic if it does not.
    }

    if (!response.ok) {
      throw new Error(result.description || result.message || 'Failed to add to cart');
    }

    return result;
  }

  async fetchCart() {
    const response = await fetch(CART_URL, {
      headers: {
        Accept: 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to refresh cart: ${response.statusText}`);
    }

    return response.json();
  }

  getDisplayedCartCount() {
    const countElement = document.querySelector('cart-icon [ref="cartBubbleCount"]');
    const count = parseInt(countElement?.textContent || '0', 10);

    return Number.isFinite(count) ? count : 0;
  }

  getCartUpdateItemCount(cart, fallbackAddedQuantity) {
    if (typeof cart?.item_count === 'number') {
      return cart.item_count;
    }

    return this.getDisplayedCartCount() + fallbackAddedQuantity;
  }

  async handleAddToCart() {
    if (!this.addToCartBtn || !this.addToCartBtnText) return;

    const variant = this.getSelectedVariant();

    if (!variant) {
      this.showError('Please make a selection for all options.');
      return;
    }

    if (!this.isVariantPurchasableForSelections(variant, this.selections)) {
      this.showError('This configuration is currently sold out.');
      return;
    }

    if (this.isSelectedVariantPreorder() && !this.preorderAccepted) {
      this.showError('Please acknowledge the preorder information before continuing.');
      return;
    }

    if (this.isOpenBoxAcknowledgementRequired() && !this.openBoxAcknowledgementAccepted) {
      this.showError(
        this.openBoxSettings.acknowledgementErrorText ||
          'Please acknowledge the Open Box terms before continuing.'
      );
      return;
    }

    if (this.isForceSelected() && !this.hasValidForceBeltSelection()) {
      this.showError('Please select a squat belt size to continue.');
      this.scrollToForceBeltStep();
      return;
    }

    // Store original button text
    if (!this.addToCartBtn.dataset.originalText) {
      this.addToCartBtn.dataset.originalText = this.addToCartBtnText.textContent.trim();
    }

    // Disable button and show loading state
    this.addToCartBtn.disabled = true;
    const isEditMode = Boolean(this.cartItemKey);
    const cartItemKeyToRemove = this.cartItemKey;
    const originalText = this.addToCartBtn.dataset.originalText || this.addToCartBtnText.textContent.trim() || 'Add to Cart';
    this.addToCartBtnText.textContent = isEditMode ? 'Updating...' : 'Adding...';

    try {
      const sectionIds = this.getCartSectionIds();
      const fallbackResolutions = await this.resolveFallbacksBeforeCart();
      const resolvedForceBelt = fallbackResolutions.get('force-belt');
      if (resolvedForceBelt?.selectedVariantId) {
        this.selectedBeltVariantId = resolvedForceBelt.selectedVariantId;
      }
      let replacementConfigurationId = '';
      if (isEditMode) {
        replacementConfigurationId = this.generateConfigurationId();
        this.configurationId = replacementConfigurationId;
      }
      const items = this.buildCartItems(variant, fallbackResolutions);
      const addedQuantity = this.getCartItemsQuantity(items);
      const result = await this.addItemsToCart(items, sectionIds);
      let cartUpdateResult = result;

      if (isEditMode) {
        const keysToRemove = [
          cartItemKeyToRemove,
          ...(this.cartCompanionMap ? [...this.cartCompanionMap.values()] : []),
        ].filter(Boolean);
        const uniqueKeysToRemove = [...new Set(keysToRemove)];

        try {
          cartUpdateResult = await this.removeCartItems(uniqueKeysToRemove, sectionIds);
          this.assertCartKeysRemoved(cartUpdateResult, uniqueKeysToRemove);
          console.info('[X3 Configurator] Removed previous cart items:', uniqueKeysToRemove);
        } catch (cleanupError) {
          console.error('[X3 Configurator] Failed to remove previous cart items:', uniqueKeysToRemove, cleanupError);

          const rollbackSucceeded = await this.rollbackConfigurationItems(replacementConfigurationId, sectionIds);
          try {
            const postRollbackCart = await this.fetchCart();
            this.dispatchCartUpdate({}, postRollbackCart, 0);
          } catch (cartError) {
            console.error('[X3 Configurator] Failed to refresh cart after rollback:', cartError);
          }

          throw new Error(
            rollbackSucceeded
              ? 'Failed to finish updating cart. Your replacement was rolled back; please try again.'
              : 'Failed to finish updating cart. Please review your cart and try again.',
            { cause: cleanupError }
          );
        }
      }

      let cart = null;

      try {
        cart = await this.fetchCart();
      } catch (cartError) {
        console.error('Cart refresh error:', cartError);
      }
      if (!cart && isEditMode && Array.isArray(cartUpdateResult?.items)) {
        cart = cartUpdateResult;
      }

      if (isEditMode) {
        this.cartItemKey = null;
        this.cartCompanionMap = null;
      }

      this.onCartUpdateSuccess(cartUpdateResult, cart, addedQuantity, isEditMode ? 'Cart Updated!' : undefined);
      this.configurationId = null;
      this.updateAddToCartButton();
    } catch (error) {
      console.error('Add to cart error:', error);
      this.showError(error.message || 'Failed to add item to cart. Please try again.');
      this.addToCartBtnText.textContent = originalText;
    } finally {
      // Re-enable button
      this.addToCartBtn.disabled = false;
    }
  }

  async removeCartItems(keys, sectionIds = []) {
    if (!keys || keys.length === 0) return null;
    const updates = {};
    keys.forEach((key) => {
      updates[key] = 0;
    });
    const body = { updates };
    if (sectionIds.length > 0) {
      body.sections = sectionIds.join(',');
      body.sections_url = window.location.pathname;
    }

    const response = await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    let result = {};

    try {
      result = await response.json();
    } catch (_) {
      // Shopify normally returns JSON here. Keep the fallback error generic if it does not.
    }

    if (!response.ok) {
      throw new Error(result.description || result.message || 'Failed to remove companion cart items');
    }

    return result;
  }

  assertCartKeysRemoved(cart, keys) {
    if (!Array.isArray(cart?.items) || !keys || keys.length === 0) return;

    const remainingKeys = keys.filter((key) => cart.items.some((item) => item.key === key));
    if (remainingKeys.length > 0) {
      throw new Error(`Failed to remove previous cart items: ${remainingKeys.join(', ')}`);
    }
  }

  getConfigurationCartItemKeys(cart, configurationId) {
    if (!configurationId || !Array.isArray(cart?.items)) {
      return [];
    }

    return cart.items
      .filter((item) => item?.properties?._x3_configuration_id === configurationId)
      .map((item) => item.key)
      .filter(Boolean);
  }

  async rollbackConfigurationItems(configurationId, sectionIds = []) {
    if (!configurationId) return false;

    try {
      const cart = await this.fetchCart();
      const keys = this.getConfigurationCartItemKeys(cart, configurationId);
      if (keys.length === 0) {
        return true;
      }

      await this.removeCartItems(keys, sectionIds);
      console.info('[X3 Configurator] Rolled back replacement cart items:', keys);
      return true;
    } catch (rollbackError) {
      console.error('[X3 Configurator] Failed to roll back replacement cart items:', rollbackError);
      return false;
    }
  }

  /**
   * Parse the "variantId:cartKey|variantId:cartKey|..." companion items string
   * from the cart trigger data attribute.
   * Returns a Map<string variantId, string cartItemKey>.
   */
  parseCompanionItems(str) {
    const map = new Map();
    if (!str) return map;
    str.split('|').forEach((entry) => {
      const colonIdx = entry.indexOf(':');
      if (colonIdx === -1) return;
      const variantId = entry.slice(0, colonIdx).trim();
      const cartKey = entry.slice(colonIdx + 1).trim();
      if (variantId && cartKey) {
        map.set(variantId, cartKey);
      }
    });
    return map;
  }

  getCompanionVariantCandidates(...variantIds) {
    const candidates = [];

    variantIds.forEach((variantId) => {
      const rawId = String(variantId || '').trim();
      if (!rawId) return;

      candidates.push(rawId);

      const numericId = String(this.parseNumericId(rawId) || this.getNumericId(rawId) || '').trim();
      if (numericId) {
        candidates.push(numericId);
      }
    });

    return [...new Set(candidates)];
  }

  getCompanionMatchKey(companionMap, candidates) {
    return candidates.find((candidate) => companionMap.has(candidate)) || null;
  }

  /**
   * Pre-apply companion cart items when opening in edit mode.
   * Matches force premium variant and addon checkbox variant IDs against the companion map,
   * then checks/selects the matching UI controls and stores the map for later removal.
   */
  applyCartCompanions(companionItemsStr) {
    const companionMap = this.parseCompanionItems(companionItemsStr);
    // Only store cart keys for items we actually recognise — prevents accidental
    // removal of unrelated products that ended up in the companion payload.
    const matchedKeys = new Map();

    // Match force premium
    if (this.isForcePremiumConfigured()) {
      const premium = this.forceConfig.premiumUpsell || {};
      const premiumNumericId = String(
        this.parseNumericId(premium.numericVariantId || premium.variantId) || ''
      );
      const isInCart = premiumNumericId ? companionMap.has(premiumNumericId) : false;
      this.forcePremiumSelected = isInCart;
      if (isInCart) {
        matchedKeys.set(premiumNumericId, companionMap.get(premiumNumericId));
      }
      if (this.forcePremiumToggle) {
        this.forcePremiumToggle.setAttribute('aria-checked', String(this.forcePremiumSelected));
      }
    } else {
      this.forcePremiumSelected = false;
    }

    // Reset and rematch addon checkboxes
    this.addonCheckboxes.forEach((checkbox) => {
      const variantId = checkbox.dataset.addonVariantId;
      const fallbackVariantId = checkbox.dataset.addonFallbackVariantId;
      const optionName = checkbox.dataset.addonOption;
      if (!variantId || !optionName) return;

      const primaryMatchKey = this.getCompanionMatchKey(companionMap, this.getCompanionVariantCandidates(variantId));
      const fallbackMatchKey = this.getCompanionMatchKey(
        companionMap,
        this.getCompanionVariantCandidates(fallbackVariantId)
      );
      const matchKey = primaryMatchKey || fallbackMatchKey;
      const isInCart = matchKey !== null;

      if (isInCart) {
        matchedKeys.set(matchKey, companionMap.get(matchKey));
      }

      this.setAddonSelectedSource(
        checkbox,
        primaryMatchKey ? 'primary' : fallbackMatchKey ? 'fallback' : this.getAddonDefaultSource(checkbox)
      );
      this.setAddonChecked(checkbox, isInCart);

      if (!this.selectedAddons[optionName]) {
        this.selectedAddons[optionName] = [];
      }
      if (isInCart) {
        if (!this.selectedAddons[optionName].includes(variantId)) {
          this.selectedAddons[optionName].push(variantId);
        }
      } else {
        this.selectedAddons[optionName] = this.selectedAddons[optionName].filter(
          (id) => id !== variantId
        );
      }
    });

    this.cartCompanionMap = matchedKeys.size > 0 ? matchedKeys : null;
    this.updateUI();
    this.updateLayers();
  }

  /**
   * Reset companion state — called when opening the modal in non-edit mode
   * or when the edit-cart event fires with no cart item.
   */
  resetCartCompanions() {
    this.cartCompanionMap = null;

    // Reset force premium to its configured default
    this.forcePremiumSelected = this.isForcePremiumConfigured();
    if (this.forcePremiumToggle) {
      this.forcePremiumToggle.setAttribute('aria-checked', String(this.forcePremiumSelected));
    }

    // Uncheck all addon checkboxes and clear selected addons state
    this.addonCheckboxes.forEach((checkbox) => {
      checkbox.checked = false;
      const card = checkbox.closest('.x3-configurator__addon-card');
      if (card) {
        card.classList.remove('x3-configurator__addon-card--selected');
      }
    });
    this.selectedAddons = {};

    this.updateUI();
    this.updateLayers();
  }

  dispatchCartUpdate(result, cart, addedQuantity) {
    // Dispatch cart:update event matching theme's CartAddEvent structure
    // This triggers cart-items-component, cart-drawer-component, and cart-icon updates
    document.dispatchEvent(
      new CustomEvent(CART_UPDATE_EVENT, {
        bubbles: true,
        detail: {
          resource: cart || result,
          sourceId: CONFIGURATOR_SOURCE_ID,
          data: {
            sections: result?.sections,
            source: CONFIGURATOR_SOURCE_ID,
            itemCount: this.getCartUpdateItemCount(cart, addedQuantity),
          },
        },
      })
    );
  }

  onCartUpdateSuccess(result, cart, addedQuantity, successMessage) {
    this.dispatchCartUpdate(result, cart, addedQuantity);

    // Show success feedback
    this.showSuccess(successMessage);
  }

  showError(message) {
    if (!this.errorContainer) return;

    const messageEl = this.errorContainer.querySelector('.x3-configurator__error-message');
    if (messageEl) {
      messageEl.textContent = message;
    }
    this.errorContainer.hidden = false;

    // Scroll error into view
    this.errorContainer.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }

  hideError() {
    if (this.errorContainer) {
      this.errorContainer.hidden = true;
    }
  }

  showSuccess(message = 'Added to Cart!') {
    if (!this.addToCartBtn || !this.addToCartBtnText) return;

    // Store original text if not already stored
    if (!this.addToCartBtn.dataset.originalText) {
      this.addToCartBtn.dataset.originalText = this.addToCartBtnText.textContent.trim();
    }

    // Temporarily change button to show success
    const originalText = this.addToCartBtn.dataset.originalText;
    this.addToCartBtnText.textContent = message;
    this.addToCartBtn.classList.add('x3-configurator__add-to-cart--success');

    setTimeout(() => {
      this.addToCartBtnText.textContent = originalText;
      this.addToCartBtn.classList.remove('x3-configurator__add-to-cart--success');
    }, 2000);
  }

  /**
   * Preload a single image into browser cache.
   * @param {string} src - Image URL to preload
   */
  preloadImage(src) {
    if (!src) return;
    const img = new Image();
    img.src = src;
  }

  /**
   * Preload all component images for snappy hover previews.
   * Called on init to load images in background before user interaction.
   */
  preloadComponentImages() {
    if (this.layersActive) return;
    const imageUrls = Object.values(this.componentImages);
    imageUrls.forEach((src) => this.preloadImage(src));
  }

  // ==========================================================================
  // ADD-ON METHODS
  // ==========================================================================

  /**
   * Handle add-on checkbox toggle.
   * Updates selection state, card styling, and UI.
   * Reads addon data from checkbox data attributes.
   *
   * @param {Event} event - Change event from checkbox
   */
  handleAddonToggle(event) {
    const checkbox = event.target;
    const variantId = checkbox.dataset.addonVariantId;
    const optionName = checkbox.dataset.addonOption;
    const card = checkbox.closest('.x3-configurator__addon-card');

    if (card?.hidden || card?.classList.contains('x3-configurator__addon-card--unavailable')) {
      this.setAddonChecked(checkbox, false);
      return;
    }

    this.setAddonSelectedSource(checkbox, this.getAddonSelectedSource(checkbox));

    // Initialize array for this option if needed
    if (!this.selectedAddons[optionName]) {
      this.selectedAddons[optionName] = [];
    }

    if (checkbox.checked) {
      // Add to selections
      if (!this.selectedAddons[optionName].includes(variantId)) {
        this.selectedAddons[optionName].push(variantId);
      }
      if (card) {
        card.classList.add('x3-configurator__addon-card--selected');
      }
    } else {
      // Remove from selections
      this.selectedAddons[optionName] = this.selectedAddons[optionName].filter(
        (id) => id !== variantId
      );
      if (card) {
        card.classList.remove('x3-configurator__addon-card--selected');
      }
    }

    // Update UI (summary, total price)
    this.updateUI();
    this.updateLayers();
    this.restoreSelectedVariantPreviewUnlessActive(card || checkbox, () => this.showAddonPreview(checkbox));
  }

  handleAddonChoiceClick(event) {
    const choice = event.currentTarget;
    const card = choice.closest('.x3-configurator__addon-card');
    const checkbox = card?.querySelector('.x3-configurator__addon-checkbox');

    event.preventDefault();
    event.stopPropagation();

    if (!checkbox || choice.hidden || choice.classList.contains('x3-configurator__addon-choice-option--unavailable')) {
      return;
    }

    this.setAddonSelectedSource(checkbox, choice.dataset.addonChoiceSource || 'primary');
    this.setAddonChecked(checkbox, true);
    this.updateUI();
    this.updateLayers();
    this.restoreSelectedVariantPreviewUnlessActive(card || checkbox, () => this.showAddonPreview(checkbox));
    this.hideError();
  }

  handleAddonChoiceKeydown(event) {
    if (event.key === 'Enter' || event.key === ' ') {
      this.handleAddonChoiceClick(event);
      return;
    }

    if (!['ArrowLeft', 'ArrowUp', 'ArrowRight', 'ArrowDown'].includes(event.key)) {
      return;
    }

    const choice = event.currentTarget;
    const card = choice.closest('.x3-configurator__addon-card');
    const choices = Array.from(card?.querySelectorAll('.x3-configurator__addon-choice-option') || []).filter(
      (option) => !option.hidden && !option.classList.contains('x3-configurator__addon-choice-option--unavailable')
    );
    const currentIndex = choices.indexOf(choice);

    if (currentIndex === -1 || choices.length <= 1) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    const direction = event.key === 'ArrowLeft' || event.key === 'ArrowUp' ? -1 : 1;
    const nextChoice = choices[(currentIndex + direction + choices.length) % choices.length];
    const checkbox = card?.querySelector('.x3-configurator__addon-checkbox');

    nextChoice.focus();
    if (!checkbox) {
      return;
    }

    this.setAddonSelectedSource(checkbox, nextChoice.dataset.addonChoiceSource || 'primary');
    this.setAddonChecked(checkbox, true);
    this.updateUI();
    this.updateLayers();
    this.restoreSelectedVariantPreviewUnlessActive(card || checkbox, () => this.showAddonPreview(checkbox));
    this.hideError();
  }

  setAddonChecked(checkbox, checked) {
    if (!checkbox) {
      return;
    }

    const variantId = checkbox.dataset.addonVariantId;
    const optionName = checkbox.dataset.addonOption;
    const card = checkbox.closest('.x3-configurator__addon-card');
    this.setAddonSelectedSource(checkbox, checked ? this.getAddonSelectedSource(checkbox) : this.getAddonDefaultSource(checkbox));
    checkbox.checked = checked;
    card?.classList.toggle('x3-configurator__addon-card--selected', checked);

    if (!optionName || !variantId) {
      return;
    }

    if (!this.selectedAddons[optionName]) {
      this.selectedAddons[optionName] = [];
    }

    if (checked) {
      if (!this.selectedAddons[optionName].includes(variantId)) {
        this.selectedAddons[optionName].push(variantId);
      }
    } else {
      this.selectedAddons[optionName] = this.selectedAddons[optionName].filter((id) => id !== variantId);
    }
  }

  /**
   * Clear all selected add-ons for a specific option.
   * Called when the main option value changes.
   *
   * @param {string} optionName - The option name (e.g., "Bands")
   */
  clearAddonsForOption(optionName) {
    // Clear state
    this.selectedAddons[optionName] = [];

    // Uncheck all checkboxes and remove selected class for this option
    this.addonCheckboxes.forEach((checkbox) => {
      if (checkbox.dataset.addonOption === optionName) {
        this.setAddonChecked(checkbox, false);
      }
    });
  }

  /**
   * Update visibility of add-on sections based on current option selections.
   * Shows only the add-on section that matches the currently selected value.
   */
  updateAddonSections() {
    this.addonSections.forEach((section) => {
      const optionName = section.dataset.addonStep;
      const addonValue = section.dataset.addonValue;
      const currentValue = this.selections[optionName];

      // Show only if this addon section matches the current selection
      const shouldShow = addonValue === currentValue;
      section.hidden = !shouldShow;
    });

    this.updateOpenBoxAddonAvailability();
  }

  updateOpenBoxAddonAvailability() {
    if (!this.isOpenBox) {
      return;
    }

    const addonContainers = this.section.querySelectorAll('.x3-configurator__addon-cards');
    addonContainers.forEach((container) => {
      const cards = Array.from(container.children).filter((child) =>
        child.classList?.contains('x3-configurator__addon-card')
      );
      const cardStates = cards
        .map((card) => {
          const checkbox = card.querySelector('.x3-configurator__addon-checkbox');
          if (!checkbox) return null;

          return {
            card,
            checkbox,
            ...this.getAddonAvailabilityState(checkbox),
          };
        })
        .filter(Boolean);

      cardStates.forEach(({ card, checkbox, available }) => {
        const shouldHide = !available;
        card.hidden = shouldHide;
        checkbox.disabled = !available;
        card.classList.toggle('x3-configurator__addon-card--unavailable', !available);

        if ((shouldHide || !available) && checkbox.checked) {
          this.setAddonChecked(checkbox, false);
        }

        this.setAddonSelectedSource(checkbox, this.getAddonSelectedSource(checkbox));
      });

      const hasVisibleAddon = cardStates.some((state) => state.available);
      const addonSection = container.closest('.x3-configurator__addon-section');
      if (addonSection) {
        const optionName = addonSection.dataset.addonStep;
        const addonValue = addonSection.dataset.addonValue;
        addonSection.hidden = this.selections[optionName] !== addonValue || !hasVisibleAddon;
        return;
      }

      const parentStep = container.closest('.x3-configurator__step');
      if (parentStep && !parentStep.dataset.step) {
        parentStep.hidden = !hasVisibleAddon;
      }
    });
  }

  /**
   * Calculate the total price of all selected add-ons.
   * Reads prices from checkbox data attributes.
   *
   * @returns {number} Total add-on price in cents
   */
  calculateAddonTotal() {
    let total = 0;

    this.addonCheckboxes.forEach((checkbox) => {
      if (checkbox.checked) {
        total += this.getAddonSelectedPriceData(checkbox).price;
      }
    });

    return total;
  }

  calculateAddonCompareAtTotal() {
    let total = 0;

    this.addonCheckboxes.forEach((checkbox) => {
      if (!checkbox.checked) {
        return;
      }

      const { price, compareAtPrice } = this.getAddonSelectedPriceData(checkbox);
      total += this.getEffectiveCompareAtPrice(compareAtPrice, price);
    });

    return total;
  }

  parsePriceValue(value) {
    const parsed = parseInt(value, 10);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  parseNumericId(value) {
    return this.parsePriceValue(this.getNumericId(value));
  }

  generateConfigurationId() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
      return `x3-${window.crypto.randomUUID()}`;
    }

    return `x3-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  }

  getOrCreateConfigurationId() {
    if (!this.configurationId) {
      this.configurationId = this.generateConfigurationId();
    }

    return this.configurationId;
  }

  getConfigurationLineProperties(configurationId) {
    return configurationId
      ? {
          _x3_configuration_id: configurationId,
        }
      : {};
  }

  getForcePremiumLineItem(configurationId = '') {
    if (!this.isForceSelected() || !this.forcePremiumSelected || !this.isForcePremiumConfigured()) {
      return null;
    }

    const premium = this.forceConfig.premiumUpsell || {};
    const variantId = this.parseNumericId(premium.numericVariantId || premium.variantId);
    const sellingPlanId = this.parseNumericId(premium.numericSellingPlanId || premium.sellingPlanId);

    if (!variantId || !sellingPlanId) {
      return null;
    }

    return {
      id: variantId,
      quantity: 1,
      selling_plan: sellingPlanId,
      properties: this.getConfigurationLineProperties(configurationId),
    };
  }

  hasValidCompareAtPrice(compareAtPrice, price) {
    return Number.isFinite(compareAtPrice) && Number.isFinite(price) && compareAtPrice > price;
  }

  getEffectiveCompareAtPrice(compareAtPrice, price) {
    return this.hasValidCompareAtPrice(compareAtPrice, price) ? compareAtPrice : price;
  }

  getSavingsPillText(savingsValue, savingsPercentage, displayAsPercentage) {
    if (displayAsPercentage) {
      return `Save ${Math.max(0, Math.round(savingsPercentage))}%`;
    }

    return `Save ${this.formatMoney(Math.max(0, savingsValue))}`;
  }

  /**
   * Update the add-on summary section in the order summary.
   * Renders selected add-ons grouped under their parent option.
   * Reads addon data from checkbox data attributes.
   */
  updateAddonSummary() {
    // Update option-specific add-on summaries
    this.options.forEach((option) => {
      this.updateAddonSummaryForKey(option.name);
    });

    // Update global additional options summary
    this.updateAddonSummaryForKey('additional-options');
  }

  updateAddonSummaryForKey(key) {
    const summaryContainer = this.section.querySelector(`[data-summary-addons="${key}"]`);
    if (!summaryContainer) return;

    const checkedAddons = Array.from(this.addonCheckboxes).filter(
      (cb) => cb.dataset.addonOption === key && cb.checked
    );

    if (checkedAddons.length === 0) {
      summaryContainer.hidden = true;
      summaryContainer.innerHTML = '';
      return;
    }

    const html = checkedAddons
      .map((checkbox) => {
        const { displayTitle: rawTitle, conditionLabel, price, compareAtPrice, displayAsPercentage } =
          this.getAddonSelectedPriceData(checkbox);
        const title = this.escapeHtml(rawTitle || checkbox.dataset.addonDisplayTitle || checkbox.dataset.addonTitle || '');
        const condition = conditionLabel ? ` (${this.escapeHtml(conditionLabel)})` : '';
        const hasSale = this.hasValidCompareAtPrice(compareAtPrice, price);
        const savingsValue = Math.max(0, compareAtPrice - price);
        const savingsPercentage = hasSale ? Math.round((savingsValue * 100) / compareAtPrice) : 0;

        return `
          <div class="x3-configurator__summary-addon-item">
            <span class="x3-configurator__summary-addon-label">+ ${title}${condition}</span>
            <span class="x3-configurator__summary-addon-meta">
              ${
                hasSale
                  ? `<span class="x3-configurator__savings-pill x3-configurator__summary-addon-savings">${this.getSavingsPillText(savingsValue, savingsPercentage, displayAsPercentage)}</span>`
                  : ''
              }
              <span class="x3-configurator__summary-addon-prices">
                <span class="x3-configurator__summary-addon-price">${this.formatMoney(price)}</span>
                ${
                  hasSale
                    ? `<span class="x3-configurator__summary-addon-compare-price">${this.formatMoney(compareAtPrice)}</span>`
                    : ''
                }
              </span>
            </span>
          </div>
        `;
      })
      .join('');

    summaryContainer.innerHTML = html;
    summaryContainer.hidden = false;
  }
}

// Initialize when DOM is ready
function initX3Configurators() {
  const sections = document.querySelectorAll(
    '.x3-configurator[data-configurator]:not([data-configurator-initialized="true"])'
  );
  sections.forEach((section) => new X3Configurator(section));
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initX3Configurators);
} else {
  initX3Configurators();
}

// Export for potential external use
window.X3Configurator = X3Configurator;
window.initX3Configurators = initX3Configurators;
